import * as THREE from 'three';
import { Visualizer } from '../../visualizer.js';
import { API_BASE } from '../../api-base.js';
import { safeZoneUniforms } from '../../shaders/safeZone.glsl.js';
import { MIXDECK_BUS, MIXDECK_BG, MIXDECK_QUAD } from '../mixdeck.js';
import { STAGE_SCREEN_FRAGMENTS } from '../stage/stage-screens.js';
import { PyroLayer } from '../../particle-layers/pyro-layer.js';
import { ConfettiLayer } from '../../particle-layers/confetti-layer.js';
import { laserClampChunk } from '../../lighting/laser-array.js';
import { Adapt } from '../lib/adapt.js';
import { VdjPost, VdjComfort, vdjColor, vdjBeatChroma, VDJ_PRESETS, VDJ_DROP_FLASH } from '../lib/vdjLook.js';
import { SceneClock } from '../lib/scene-clock.js';
import { Font } from 'three/examples/jsm/loaders/FontLoader.js';

// PHOTOSTAGE: a real stage photographed three times (everything off, the LED
// screens white, the projectors white), turned into a live concert capture.
// The photograph gives the realism; everything procedural gives the life:
//
//   colour = dark
//          + sum over screen groups g of S_g x mean colour of what screen g shows
//          + W x colour of the zone x its level (six zones: left/centre/right x top/floor)
//          + the screens' content, through each screen's homography and its mask
//            (what stood in front of a screen when it was lit is not in the mask:
//            the DJ, the console, the heads stay in front)
//          + beams, lasers, CO2 (half-res pass in the AIR mask, hidden by what
//            is nearer), flames (PyroLayer at the lip), confetti (ConfettiLayer
//            in 3D, occluded by the crowd through a depth-only quad), the arms
//            and phones of the crowd (instanced capsules, R13), the DJ's nod.
//
// The base stays photographic and sharp: layers are added, nothing blurs it.
// The light layers S and W, the masks and the depth come from
// tools/build-photostage.mjs (public/textures/photostage.<scene>.<hash>.*).
//
// THE SHOTS are framings of the same image in 2.5D (depth parallax, bounded
// 2 %, inverse mapping so a disoccluded pixel stretches its edge, never
// tears). The magnification is capped at max(1.04, sourceWidth / outputWidth)
// (R1: never more than one output pixel per texel; the 4 % margin carries the
// slow zoom, the kick and the parallax). With 1376 px sources at 1080p every
// close shot therefore collapses to the wide frame, and the build says so.
//
// Every photoStage visualizer is one thin configuration of this class:
// assets (scene letter), light programme, palettes, screen mix, shots. One
// scene is resident on the GPU at a time: everything loads in init() and is
// released in dispose().
//
// THE FOREGROUND CROWD (2026-10-21): a warped photograph can only move a few
// pixels before it tears, so the photographed crowd stays a background
// (its depth bands still bounce); in front of it, three rows of people cut
// out of green-screen photographs (tools/build-crowd.mjs: 24 people, arms
// down / arms up, aligned on the torso, plus a rim layer) jump, raise their
// arms and sway. Instanced quads drawn after the composite, so in front of
// the beams, the lasers, the flames and the confetti; a per-person state
// texture (bounce, arms, sway, tilt) is the only per-frame upload. Rows
// (head top, shoulder width, in screen-height units): 3 at 0.745 / 0.10,
// 2 at 0.80 / 0.14, 1 at 0.875 / 0.20 - the front heads stay under 85 % of
// the height except on the drop's jump, the overlay keeps its centre. The
// bodies are darkened by row to the photograph's blacks; their rim takes
// the current light (zones + screens + flashes), sampled by x.
//
// Debug (common to the three): ?stageshot=wide|dj|left|right|crowd|low,
// ?stagelayers=1 (S and W), ?stagemasks=1, ?stageheads=1, ?stagedrop=1
// (a drop at 3 s then every 20 s), ?crowdrows=0|1|2|3 (front rows shown),
// ?crowdpose=a|b (every arm down / up), ?crowdatlas=1 (the sprite atlas).

const MANIFEST_URL = 'textures/photostage.json';
const MAX_SCREENS = 12, MAX_BEAMS = 32, MAX_LASERS = 16, MAX_FX = 8, BANDS = 5;
const CONTENT_RES = { 0: [512, 288], 1: [768, 432], 2: [1024, 576] };   // the content targets: tall enough for the side screens (a 160 px target made stairs)
const SCENE_S = 20, BUILD_BEATS = 16;
const ZOOM_MARGIN = 1.04;
const PROGRAMS = ['fan', 'ballyhoo', 'cross', 'converge', 'sweep', 'wide'];
// BAS (the big foreground silhouettes) is withdrawn: never convincing on these photographs (correction of 2026-10-19)
const SHOTS = {
  wide: { c: [0.5, 0.5], z: 1.0 }, dj: { c: null, z: 1.9 }, left: { c: [0.3, 0.48], z: 1.6 }, right: { c: [0.7, 0.48], z: 1.6 },
  crowd: { c: [0.5, 0.72], z: 1.5 }
};
const FONT_FILE = 'fonts/typeclash-condensed.typeface.json';          // titles
const FONT_BOLD = 'fonts/typeclash-grotesk.typeface.json';            // the artist: very bold capitals (Archivo Black)
const STACK_W = 512, STACK_H = 2048;
const LAYOUTS = ['giant', 'panorama', 'contour', 'scroll', 'letters', 'stacked', 'glitch'];
const CONTENT_WEIGHTS = { artist: 0.5, title: 0.2, cover: 0.2, gen: 0.1 };
const TITLE_W = 2048, TITLE_H = 512;
const PHONES_MAX = 25;
const CROWD_URL = 'textures/crowd.json';
// the foreground rows, back to front: count range, head top (screen height, down), shoulder width (screen height), body gain, rim gain, bounce, jump
const CROWD_ROWS = [
  { n: [16, 20], y: 0.745, sh: 0.10, body: 0.07, rim: 0.55, bounce: 0.006, jump: 0.016 },
  { n: [12, 14], y: 0.80, sh: 0.14, body: 0.10, rim: 0.75, bounce: 0.010, jump: 0.027 },
  { n: [8, 10], y: 0.875, sh: 0.20, body: 0.14, rim: 0.95, bounce: 0.015, jump: 0.040 }
];
const CROWD_MAX = 48;
const ZOOM_SMALL_SOURCE = 1.15;   // a source narrower than the output: tight shots stop here (the brief's limit)
const _fontPromises = {};
function loadFont(file) {
  file = file || FONT_FILE;
  if (!_fontPromises[file]) _fontPromises[file] = fetch(file).then((r) => { if (!r.ok) throw new Error(FONT_FILE + ' HTTP ' + r.status); return r.json(); }).then((j) => new Font(j)).catch((e) => { console.warn('[photostage] typeface missing, titles off', e); return null; });
  return _fontPromises[file];
}

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(x) { x = clamp01(x); return x * x * (3 - 2 * x); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function artProxyUrl(u) { return /^data:/i.test(u) ? u : API_BASE + '/api/art-proxy?url=' + encodeURIComponent(u); }
function mulberry(seed) {
  let a = seed >>> 0;
  return function () { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// --- the assets --------------------------------------------------------------------------
let _manifest = null;
function manifest() {
  if (!_manifest) _manifest = fetch(MANIFEST_URL).then((r) => { if (!r.ok) throw new Error('photostage.json HTTP ' + r.status); return r.json(); }).catch((e) => { console.warn('[photostage] manifest unavailable', e); return { scenes: {} }; });
  return _manifest;
}
// WIRING POINT for the entry points: resolves the ids whose scene is NOT built,
// so main/main-radio/play-boot can withdraw them (disableVisualization).
export function preloadPhotoStage(ids) {
  return manifest().then((m) => {
    const missing = [];
    for (const [id, scene] of Object.entries(ids)) if (!m.scenes || !m.scenes[scene]) { console.warn('[photostage] ' + id + ' indisponible : scène ' + scene + ' absente du manifeste (tools/build-photostage.mjs)'); missing.push(id); }
    return missing;
  });
}
let _crowd = null;
function loadCrowd() {
  if (!_crowd) _crowd = fetch(CROWD_URL).then((r) => { if (!r.ok) throw new Error('crowd.json HTTP ' + r.status); return r.json(); })
    .then((m) => Promise.all([loadTexture('textures/' + m.color, photo), loadTexture('textures/' + m.mask, mask)]).then(([color, msk]) => ({ m, color, mask: msk })))
    .catch((e) => { console.warn('[photostage] foule de premier plan indisponible', e); _crowd = null; return null; });
  return _crowd;
}
function loadTexture(url, configure) {
  return new Promise((resolve, reject) => { new THREE.TextureLoader().load(url, (t) => { configure(t); t.needsUpdate = true; resolve(t); }, undefined, () => reject(new Error(url + ' failed to load'))); });
}
const photo = (t) => { t.colorSpace = THREE.SRGBColorSpace; t.generateMipmaps = false; t.minFilter = t.magFilter = THREE.LinearFilter; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; };
const mask = (t) => { t.colorSpace = THREE.NoColorSpace; t.generateMipmaps = false; t.minFilter = t.magFilter = THREE.LinearFilter; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; };
const data = (t) => { t.colorSpace = THREE.NoColorSpace; t.generateMipmaps = false; t.minFilter = t.magFilter = THREE.NearestFilter; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; };
async function loadScene(scene) {
  const m = await manifest();
  const sc = m.scenes && m.scenes[scene];
  if (!sc) throw new Error('scène ' + scene + ' absente du manifeste');
  const [dark, layers, wash, masks, masksId, depth, headsBuf, info] = await Promise.all([
    loadTexture('textures/' + sc.dark, photo), loadTexture('textures/' + sc.layers, mask), loadTexture('textures/' + sc.wash, mask),
    loadTexture('textures/' + sc.masks, mask), loadTexture('textures/' + sc.masks, data), loadTexture('textures/' + sc.depth, data),
    fetch('textures/' + sc.heads).then((r) => { if (!r.ok) throw new Error(sc.heads + ' HTTP ' + r.status); return r.arrayBuffer(); }),
    fetch('textures/' + sc.data).then((r) => { if (!r.ok) throw new Error(sc.data + ' HTTP ' + r.status); return r.json(); })
  ]);
  return { dark, layers, wash, masks, masksId, depth, heads: new Float32Array(headsBuf), info, manifest: sc };
}

// --- shaders -----------------------------------------------------------------------------
const quadVertex = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// the shared view: output uv -> image uv (u right, v DOWN), with parallax and the crowd's bounce
const VIEW_CHUNK = `
  uniform sampler2D uDepth;        // R hi, G lo proximity; B structure
  uniform sampler2D uField;        // the per-head field (R du, G dv), source space
  uniform sampler2D uMasks;        // R screen id x16, G screen alpha, B DJ, A crowd
  uniform vec2 uCentre;            // the view's centre, image uv
  uniform vec2 uScale;             // image uv per output uv
  uniform vec2 uParallax;          // the parallax shift at proximity 1 (image uv)
  uniform float uProxRef;          // the proximity that does not move
  uniform float uNod;              // the DJ's nod (image v)
  uniform float uProxCrowd;        // the proximity where the crowd starts
  uniform vec2 uBand[${BANDS}];    // per depth band of the crowd: (du, dv) in image uv - the rows bounce in blocks, each with its phase
  vec2 flipV(vec2 img) { return vec2(img.x, 1.0 - img.y); }
  float proxAt(vec2 img) { vec4 d = texture2D(uDepth, flipV(img)); return d.r + d.g / 255.0; }
  vec2 bandOff(float b) {
    vec2 o = uBand[0];
    for (int i = 1; i < ${BANDS}; i++) if (abs(b - float(i)) < 0.5) o = uBand[i];
    return o;
  }
  vec2 viewImg(vec2 o) {
    vec2 img = uCentre + (o - 0.5) * uScale;
    float p = proxAt(img);
    img += (p - uProxRef) * uParallax;
    vec4 m = texture2D(uMasks, flipV(img));
    // the crowd's rows: 5 bands by proximity, each bouncing as a block (up from rest: the source is sampled LOWER)
    float b = clamp(floor((p - uProxCrowd) / max(1e-3, 1.0 - uProxCrowd) * ${BANDS}.0), 0.0, ${BANDS}.0 - 1.0);
    img += bandOff(b) * m.a;
    // then each head and hand on its own (the field), and the DJ's nod
    vec2 fld = texture2D(uField, flipV(img)).rg;
    img += fld + vec2(0.0, m.b * uNod);
    return clamp(img, 0.0, 1.0);
  }
`;
const compositeFragment = `
  precision highp float;
  ${VIEW_CHUNK}
  uniform sampler2D uDark, uLayers, uWash, uBeams, uContentA, uContentB, uCover, uTitle, uArtist, uStack, uDigits;
  uniform float uSMax, uWMax, uHasCover, uCoverAsp, uTitleAsp, uStackAsp, uCount, uTime, uFlash, uGain, uAspect, uDbg, uSky, uProxSky;
  uniform vec3 uGroupCol[4];       // the colour of each screen group's light (linear)
  uniform float uGroupGain[4];
  uniform vec3 uZoneCol[6];        // top L, top C, top R, floor L, floor C, floor R
  uniform float uZoneLvl[6];
  uniform mat3 uScreenH[${MAX_SCREENS}];
  uniform vec4 uScreenInfo[${MAX_SCREENS}];   // group, mode (0 generator, 1 cover, 2 title, 3 artist), row span x0, x1 (side screens offset by +-2)
  uniform float uScreenN, uSideMap, uScreenBright, uScreenFlash, uCrowdRim, uDjRim, uSharp, uFlameGlow;
  uniform float uLayout, uBg, uRevealX, uKick, uScroll, uFillPhase, uRowAsp;
  uniform vec2 uArtistSpan, uTitleSpan;   // where the text actually sits in its target (x0, x1)
  uniform vec3 uTextCol;
  // the giant fit: the text's span fills 90 % of the screen's width, its height capped at 85 % (then the width shrinks with it)
  vec2 giantFit(float asp, vec2 span) {
    float sw = max(0.05, span.y - span.x);
    float th = min(0.85, 0.9 * asp / (uTitleAsp * sw));
    float tw = th * uTitleAsp * sw / asp;
    return vec2(tw, th);
  }
  vec2 giantT(vec2 uv, vec2 fit, vec2 span) { return vec2(span.x + ((uv.x - 0.5) / fit.x + 0.5) * (span.y - span.x), (uv.y - 0.5) / fit.y + 0.5); }
  uniform vec2 uImgPx;             // the source size in pixels
  uniform vec2 uOutPx;             // the output size in pixels
  uniform vec2 uSafeCenter, uSafeHalfSize; uniform float uSafeFeather;
  varying vec2 vUv;
  float safeAtt(vec2 o) {
    vec2 s = (o * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.35, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec3 groupCol(float g) { vec3 c = uGroupCol[0]; for (int i = 1; i < 4; i++) if (abs(g - float(i)) < 0.5) c = uGroupCol[i]; return c; }
  vec3 coverFit(vec2 uv, float asp, float lod) {
    // R18: the cover covers the screen (scale = max)
    vec2 s = asp >= uCoverAsp ? vec2(1.0, uCoverAsp / asp) : vec2(asp / uCoverAsp, 1.0);
    vec2 c = (uv - 0.5) * s + 0.5;
    return texture2D(uCover, vec2(c.x, 1.0 - c.y), lod).rgb;
  }
  // a text target sampled at t (0..1, v down); 0 outside
  float textAt(sampler2D tex, vec2 t) { return (t.x < 0.0 || t.x > 1.0 || t.y < 0.0 || t.y > 1.0) ? 0.0 : texture2D(tex, vec2(t.x, 1.0 - t.y)).r; }
  float outlineAt(sampler2D tex, vec2 t, vec2 e) {
    float a = textAt(tex, t);
    float m = min(min(textAt(tex, t + vec2(e.x, 0.0)), textAt(tex, t - vec2(e.x, 0.0))), min(textAt(tex, t + vec2(0.0, e.y)), textAt(tex, t - vec2(0.0, e.y))));
    return clamp(a - m, 0.0, 1.0);
  }
  // the name on a screen: the layout decides where the text target lands on this screen (uv 0..1, v down, asp = w/h), rowX = its place over the row
  vec3 artistOn(vec2 uv, float asp, float rowX, vec3 bg, float cellPx) {
    float L = uLayout;
    bool vertical = asp < 0.8;
    vec2 t; float a = 0.0; vec3 col = uTextCol;
    if (vertical && (L > 4.5 && L < 5.5)) {
      // STACKED: the letters one above the other, 90 % of the height
      float sw = min(0.9, 0.9 * uStackAsp / asp);
      t = vec2((uv.x - 0.5) / sw + 0.5, (uv.y - 0.5) / 0.9 + 0.5);
      a = textAt(uStack, t);
    } else if (L > 0.5 && L < 1.5) {
      // PANORAMA: one image across the row of screens; the name fills the row's width even if its letters overflow the screens' height (cap 1.6)
      float sw = max(0.05, uArtistSpan.y - uArtistSpan.x);
      float th = min(1.6, 0.9 * uRowAsp / (uTitleAsp * sw)), tw = th * uTitleAsp * sw / uRowAsp;
      t = giantT(vec2(rowX, uv.y), vec2(tw, th), uArtistSpan);
      a = textAt(uArtist, t);
    } else if (vertical) {
      // a vertical screen in any other layout: the stacked name, dimmer, so the panel is never a flat
      float sw = min(0.9, 0.9 * uStackAsp / asp);
      t = vec2((uv.x - 0.5) / sw + 0.5, (uv.y - 0.5) / 0.9 + 0.5);
      a = textAt(uStack, t) * 0.75;
    } else {
      // GIANT and its variants: the name's extent over 90 % of the width
      vec2 fit = giantFit(asp, uArtistSpan); float tw = fit.x, th = fit.y;
      vec2 u2 = uv;
      if (L > 5.5) {
        // GLITCH: slices shifted on the kick, RGB split
        float row = floor(uv.y * 14.0);
        u2.x += (hash(vec2(row, floor(uTime * 9.0))) - 0.5) * 0.12 * uKick;
      }
      if (L > 2.5 && L < 3.5) {
        // SCROLL: the name repeated, running along the band
        float sw = uArtistSpan.y - uArtistSpan.x;
        float xs = (uv.x - 0.5) / tw + 0.5 - uScroll;
        t = vec2(uArtistSpan.x + fract(xs / 1.25) * 1.25 * sw, (uv.y - 0.5) / th + 0.5);
        a = textAt(uArtist, t);
      } else {
        t = giantT(u2, fit, uArtistSpan);
        if (L > 1.5 && L < 2.5 && uFillPhase > 0.5) a = outlineAt(uArtist, t, vec2(2.5 / 2048.0 * 4.0, 2.5 / 512.0 * 4.0) * (1.0 + cellPx * 0.0)) ;   // CONTOUR on the off beats
        else a = textAt(uArtist, t);
        if (L > 3.5 && L < 4.5 && t.x > uRevealX) a = 0.0;            // LETTERS: revealed one by one on the eighths
        if (L > 5.5) {
          float sh = 0.012 * uKick;
          float ar = textAt(uArtist, t + vec2(sh, 0.0)), ab = textAt(uArtist, t - vec2(sh, 0.0));
          vec3 split = vec3(ar, a, ab);
          return mix(bg, col * 1.6, split);
        }
      }
    }
    return mix(bg, col * 1.6, a);   // the letters over 1: white that stays white through the filmic curve
  }
  vec3 screenContent(vec2 uv, vec4 info, float asp, float cellPx, float rowX) {
    vec3 c;
    float mode = info.y;
    bool side = info.z < -1.5 || info.w > 1.5;
    // the background of a text: black, a palette flat, or the cover very dark and blurred (R18)
    vec3 bg = uBg < 0.5 ? vec3(0.015) : (uBg < 1.5 ? groupCol(info.x) * 0.22 : (uHasCover > 0.5 ? coverFit(uv, asp, 5.0) * 0.22 : vec3(0.02)));
    if (mode > 0.5 && mode < 1.5 && uHasCover > 0.5) {
      c = coverFit(uv, asp, 0.0) * 1.1;
    } else if (mode > 2.5) {
      c = artistOn(uv, asp, rowX, bg, cellPx);
    } else if (mode > 1.5) {
      // the TITLE in giant letters (geometry-rendered glyphs, R21): 90 % of the width on the wide screens, stacked on the vertical ones
      if (asp >= 1.0) {
        vec2 fit = giantFit(asp, uTitleSpan);
        c = mix(bg, uTextCol * 1.6, textAt(uTitle, giantT(uv, fit, uTitleSpan)));
      } else c = mix(bg, uTextCol, textAt(uStack, vec2((uv.x - 0.5) / min(0.9, 0.9 * uStackAsp / asp) + 0.5, (uv.y - 0.5) / 0.9 + 0.5)) * 0.75);
    } else {
      // the generators: one image over the row (clamped: no wrap seam), the mirror of the centre, or each side its own image
      float x = rowX;
      if (uSideMap > 0.5 && side) x = 1.0 - abs(fract(x * 0.5 + 0.5) * 2.0 - 1.0);
      if (uSideMap > 1.5 && side) x = uv.x;
      x = clamp(x, 0.0, 1.0);
      c = (uSideMap > 1.5 && side) ? texture2D(uContentB, vec2(x, 1.0 - uv.y)).rgb : texture2D(uContentA, vec2(x, 1.0 - uv.y)).rgb;
      c *= 1.35;
    }
    // the countdown digit over the centre screen
    if (uCount > 0.5 && info.x > 0.5 && info.x < 1.5) {
      float h = 0.7, w = h / asp * 0.75;
      vec2 t = vec2((uv.x - 0.5) / w + 0.5, (uv.y - 0.5) / h + 0.5);
      c *= 0.3;   // the countdown owns the centre screen: the content dims under the digit
      if (t.x >= 0.0 && t.x <= 1.0 && t.y >= 0.0 && t.y <= 1.0) { float a = texture2D(uDigits, vec2((t.x + uCount - 1.0) / 4.0, 1.0 - t.y)).r; c = mix(c, vec3(1.4), a); }
    }
    // the LED filter: a grain whose contrast fades with the LED cell size on screen (nothing under 3 px: no moire), blacks not quite black
    float g = hash(floor(uv * vec2(192.0, 108.0)));
    float k = smoothstep(3.0, 7.0, cellPx) * 0.18;
    c = c * (1.0 - k + 2.0 * k * g) * 0.92 + 0.012;
    c = mix(c, vec3(1.0), uScreenFlash);
    return c * uScreenBright;
  }
  void main() {
    vec2 o = vec2(vUv.x, 1.0 - vUv.y);
    vec2 img = viewImg(o);
    vec2 f = flipV(img);
    vec3 dark = texture2D(uDark, f).rgb;
    // a magnified photograph is sampled bilinearly: a light unsharp mask (4 taps, one output pixel) gives its edges back
    if (uSharp > 0.001) {
      vec2 e = uScale / uOutPx;
      vec3 nb = texture2D(uDark, f + vec2(e.x, 0.0)).rgb + texture2D(uDark, f - vec2(e.x, 0.0)).rgb + texture2D(uDark, f + vec2(0.0, e.y)).rgb + texture2D(uDark, f - vec2(0.0, e.y)).rgb;
      dark = max(vec3(0.0), dark * (1.0 + 4.0 * uSharp) - nb * uSharp);
    }
    vec4 L = texture2D(uLayers, f); L = L * L * uSMax;
    vec4 Wt = texture2D(uWash, f); float W = Wt.r * Wt.r * uWMax; float xw = Wt.g, yw = Wt.b, air = Wt.a;
    vec4 M = texture2D(uMasks, f);
    float structure = texture2D(uDepth, f).b;
    float prox = proxAt(img);
    vec3 col = dark;
    // the rims: the crowd's and the DJ's upper edges
    vec2 e = vec2(1.5 / uImgPx.x, 1.5 / uImgPx.y);
    float cUp = texture2D(uMasks, f + vec2(0.0, e.y)).a;
    float rimC = smoothstep(0.1, 0.6, max(0.0, cUp - M.a)) * M.a;
    float dUp = texture2D(uMasks, f + vec2(0.0, e.y)).b;
    float rimD = smoothstep(0.1, 0.6, max(0.0, dUp - M.b)) * M.b;
    // S and W land on the STAGE and on the crowd's rim: the crowd's dark mass stays dark, the open air keeps its blacks (the lit haze is the beams' pass, in the AIR mask only)
    float onStage = (1.0 - 0.85 * M.a) + rimC * 2.0;
    onStage *= 1.0 - 0.6 * air * (1.0 - structure);
    float sky = uSky * air * (1.0 - smoothstep(uProxSky - 0.02, uProxSky + 0.06, prox));
    onStage *= 1.0 - sky;
    col += (L.r * uGroupCol[0] * uGroupGain[0] + L.g * uGroupCol[1] * uGroupGain[1] + L.b * uGroupCol[2] * uGroupGain[2] + L.a * uGroupCol[3] * uGroupGain[3]) * onStage;
    vec3 top = mix(mix(uZoneCol[0] * uZoneLvl[0], uZoneCol[1] * uZoneLvl[1], smoothstep(0.2, 0.5, xw)), uZoneCol[2] * uZoneLvl[2], smoothstep(0.5, 0.8, xw));
    vec3 flo = mix(mix(uZoneCol[3] * uZoneLvl[3], uZoneCol[4] * uZoneLvl[4], smoothstep(0.2, 0.5, xw)), uZoneCol[5] * uZoneLvl[5], smoothstep(0.5, 0.8, xw));
    vec3 washCol = mix(flo, top, smoothstep(0.3, 0.7, yw));
    col += W * washCol * onStage;
    // the flames light the crowd orange: its rim and its mass, briefly
    col += vec3(1.0, 0.45, 0.12) * uFlameGlow * (rimC * 1.2 + M.a * 0.08 * (1.0 - smoothstep(uProxCrowd, 1.0, prox)));
    // THE SCREENS, by their quadrilaterals (analytic edge, 1 px anti-aliasing): the pixel belongs to the screen it is deepest inside
    float bestD = -1e9; int bestK = -1; vec2 bestUv = vec2(0.0); vec4 bestInfo = vec4(0.0);
    vec2 p = img * uImgPx;
    for (int k = 0; k < ${MAX_SCREENS}; k++) {
      if (float(k) >= uScreenN) break;
      vec3 q = uScreenH[k] * vec3(p, 1.0);
      vec2 suv = q.xy / q.z;
      vec2 fw = fwidth(suv) + 1e-6;
      float dPx = min(min(suv.x, 1.0 - suv.x) / fw.x, min(suv.y, 1.0 - suv.y) / fw.y);
      if (dPx > bestD) { bestD = dPx; bestK = k; bestUv = suv; bestInfo = uScreenInfo[k]; }
    }
    if (bestK >= 0 && bestD > -1.0) {
      float inside = smoothstep(-0.5, 0.5, bestD);
      // the rasterised mask only cuts what stands IN FRONT of the screen (the DJ, the console, raised hands), never its edge
      float occl = smoothstep(0.25, 0.75, M.g);
      float alpha = inside * mix(1.0, occl, smoothstep(2.0, 6.0, bestD));
      // the screen's size on the output, for the LED cell size and its aspect
      float scrW = 1.0 / max(1e-4, length(vec2(uScreenH[0][0][0], uScreenH[0][1][0]))) / uImgPx.x / uScale.x;
      float asp = 1.0; float cellPx = 6.0;
      for (int k = 0; k < ${MAX_SCREENS}; k++) if (k == bestK) {
        float lx = length(vec2(uScreenH[k][0][0], uScreenH[k][1][0])), ly = length(vec2(uScreenH[k][0][1], uScreenH[k][1][1]));
        asp = ly / max(1e-5, lx);
        cellPx = (1.0 / max(1e-4, lx)) / uImgPx.x / uScale.x * uOutPx.x / 192.0;
      }
      bool side = bestInfo.z < -1.5 || bestInfo.w > 1.5;
      float rowX = mix(bestInfo.z, bestInfo.w, bestUv.x) + (side ? (bestInfo.z < 0.0 ? 2.0 : -2.0) : 0.0);
      vec3 sc = screenContent(bestUv, bestInfo, max(asp, 1e-3), cellPx, rowX);
      // the LED wall's frame: a 2 px dark rim along the quadrilateral
      float frame = 1.0 - smoothstep(1.5, 2.5, bestD);
      sc = mix(sc, vec3(0.01), frame);
      col = mix(col, sc, alpha);
    }
    // the rims take the light of the screens and the projectors
    vec3 lightCol = (uGroupCol[1] * uGroupGain[1] + washCol * 0.6);
    col += rimC * lightCol * uCrowdRim;
    col += rimD * uGroupCol[1] * uDjRim;
    // beams, lasers, CO2 (half-res pass, output space)
    col += texture2D(uBeams, vUv).rgb;
    // debug views
    if (uDbg > 0.5 && uDbg < 1.5) col = vec3(L.r + L.g, L.b + L.a, W);
    if (uDbg > 1.5 && uDbg < 2.5) col = vec3(M.g, M.b, M.a) * 0.8 + vec3(air * 0.2, 0.0, structure * 0.5);
    if (uDbg > 2.5) { vec2 fl = texture2D(uField, f).rg; col = dark * 0.5 + vec3(1.0, 0.25, 0.1) * abs(fl.g) * 60.0 + vec3(0.1, 0.4, 1.0) * abs(fl.r) * 120.0; }
    col = mix(col, vec3(1.0), uFlash);
    gl_FragColor = vec4(col * safeAtt(vUv) * uGain, 1.0);   // the safe zone is in y-UP units: vUv, not o
  }
`;
const beamsFragment = `
  precision highp float;
  ${VIEW_CHUNK}
  ${laserClampChunk('0.95')}
  uniform sampler2D uWash, uNoise;
  uniform vec2 uO[${MAX_BEAMS}];          // lens, image uv
  uniform vec2 uD[${MAX_BEAMS}];          // direction (image, v down), unit
  uniform vec4 uC[${MAX_BEAMS}];          // colour, level
  uniform float uSpread[${MAX_BEAMS}];
  uniform float uN, uTime, uHaze, uAspect, uBeamProx, uSky, uProxSky;
  uniform vec4 uLaser[${MAX_LASERS}];     // ox, oy, angle, level
  uniform vec3 uLaserCol; uniform float uLaserN, uSheet, uSheetY;
  uniform vec4 uCo2[${MAX_FX}];           // x, y, age (s), level
  uniform float uCo2N; uniform vec2 uOutPx;
  varying vec2 vUv;
  void main() {
    vec2 o = vec2(vUv.x, 1.0 - vUv.y);
    vec2 img = viewImg(o);
    vec2 f = flipV(img);
    // the rigs hang IN FRONT of the screens: their beams cross the screens too (the screen mask already
    // leaves out what stands in front of a screen - the DJ, the console - and the DJ mask is taken out again)
    vec4 Mk = texture2D(uMasks, flipV(img));
    float air = max(texture2D(uWash, f).a, Mk.g * (1.0 - Mk.b));
    float prox = proxAt(img);
    if (air < 0.003) { gl_FragColor = vec4(0.0); return; }
    // what is nearer than the beams hides them; and nothing lands on the heads: the crowd mask, looked up 2 % of the height BELOW too (the margin above its top edge)
    float crowdNear = max(Mk.a, texture2D(uMasks, flipV(img + vec2(0.0, 0.02))).a);
    float vis = air * (1.0 - smoothstep(uBeamProx, uBeamProx + 0.1, prox)) * (1.0 - crowdNear);
    float sky = uSky * (1.0 - smoothstep(uProxSky - 0.02, uProxSky + 0.06, prox));
    vec2 p = vec2(img.x * uAspect, img.y);
    float n1 = texture2D(uNoise, img * vec2(1.6, 0.9) + vec2(uTime * 0.013, -uTime * 0.006)).r;
    float n2 = texture2D(uNoise, img * vec2(3.1, 2.2) + vec2(-uTime * 0.02, uTime * 0.01)).r;
    float haze = (0.12 + 1.6 * n1 * n2) * uHaze;   // no flat veil: the haze is its own structure
    vec3 col = vec3(0.0);
    for (int i = 0; i < ${MAX_BEAMS}; i++) {
      if (float(i) >= uN) break;
      vec4 C = uC[i]; if (C.w < 0.002) continue;
      vec2 O = vec2(uO[i].x * uAspect, uO[i].y);
      vec2 D = normalize(vec2(uD[i].x * uAspect, uD[i].y));
      vec2 d = p - O;
      float t = dot(d, D);
      float s = d.x * D.y - d.y * D.x;
      // the lens: a sharp glare, brighter when it points at us (D toward the camera = large |D.y| toward the crowd)
      float r = length(d);
      col += C.rgb * C.w * (exp(-r * r * 2.6e4) * 1.6 + exp(-r * 90.0) * 0.25);
      if (t <= 0.0) continue;
      float hw = 0.006 + uSpread[i] * t;
      float a = abs(s) / hw;
      if (a > 1.0) continue;
      float prof = (1.0 - a) * (1.0 - a) * (0.4 + 0.6 * (1.0 - a)) + 0.8 * exp(-a * a * 30.0);   // a sharp core down the middle
      float along = exp(-t * 1.8) * (1.0 - smoothstep(0.7, 1.1, t));
      col += C.rgb * C.w * prof * along * haze * 0.9 / (0.5 + hw * 40.0);
    }
    col *= vis * (1.0 - 0.8 * sky);
    // the lasers: lines with a pixel width (R1), a near-white core
    if (uLaserN > 0.5) {
      vec3 lc = vec3(0.0);
      vec2 pxv = uOutPx / uScale;   // output pixels per image uv
      for (int i = 0; i < ${MAX_LASERS}; i++) {
        if (float(i) >= uLaserN) break;
        vec4 Lz = uLaser[i]; if (Lz.w < 0.002) continue;
        vec2 O = Lz.xy; vec2 D = vec2(cos(Lz.z), sin(Lz.z));
        vec2 d = img - O;
        float t = dot(d, D); if (t <= 0.0) continue;
        float sPx = abs(d.x * D.y - d.y * D.x) * pxv.y;
        float core = exp(-sPx * sPx / 1.6), sheath = exp(-sPx / 3.5) * 0.35, halo = exp(-sPx / 12.0) * 0.12;
        lc += (mix(uLaserCol, vec3(1.0), 0.85) * core + uLaserCol * (sheath + halo)) * Lz.w * exp(-t * 1.2);
      }
      // the sheets over the crowd: near-horizontal lines just above the heads
      if (uSheet > 0.002) {
        for (int k = 0; k < 4; k++) {
          float y = uSheetY - float(k) * 0.022 + 0.004 * sin(img.x * 9.0 + uTime * 2.0 + float(k));
          float sPx = abs(img.y - y) * pxv.y;
          lc += (mix(uLaserCol, vec3(1.0), 0.85) * exp(-sPx * sPx / 1.6) + uLaserCol * exp(-sPx / 4.0) * 0.3) * uSheet * (0.9 - 0.15 * float(k));
        }
      }
      col += clampLuminance(lc) * vis;
    }
    // the CO2 columns: white plumes rising from the effect points, 1.6 s
    for (int i = 0; i < ${MAX_FX}; i++) {
      if (float(i) >= uCo2N) break;
      vec4 c = uCo2[i]; if (c.w < 0.002 || c.z < 0.0 || c.z > 1.8) continue;
      float h = 0.28 * (1.0 - exp(-c.z * 3.0));
      float dy = c.y - img.y;                        // up from the nozzle (v down)
      if (dy < 0.0 || dy > h) continue;
      float w = 0.012 + 0.05 * dy / 0.28 + 0.02 * c.z;
      float dx = abs(img.x - c.x) * uAspect;
      float body = (1.0 - smoothstep(w * 0.5, w, dx)) * (1.0 - smoothstep(h * 0.6, h, dy)) * (0.5 + 0.5 * n2);
      col += vec3(0.9, 0.95, 1.0) * body * c.w * 1.2 * (1.0 - smoothstep(1.0, 1.8, c.z)) * air;
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;
// the bounce field: one gaussian bump per head, drawn as points into a source-space target
const fieldVertex = `
  precision highp float;
  attribute vec4 aHead;            // u, v, r (image height units), seed
  attribute float aHand;           // 1 for the maxima at the top of the crowd: the raised hands of the photograph
  uniform float uBeatPhase, uBeats, uAmp, uJump, uJumpT, uCalm, uFieldH, uAspect, uTime, uSway;
  varying float vAmp; varying float vR; varying float vDu;
  void main() {
    float ph = fract(uBeatPhase + aHead.w * 0.35 + 0.5 * step(0.5, fract(aHead.w * 7.0)));   // variable phase, half the crowd on the off-beat
    float s = sin(3.14159 * ph);
    float amp = uAmp * (0.6 + 0.8 * fract(aHead.w * 13.0)) * s * s * aHead.z;
    // the collective jump: a wave from the centre, 0.4 s per 0.5 of image width
    float dist = length(vec2((aHead.x - 0.5) * uAspect, (aHead.y - 0.75) * 0.6));
    float tj = uJumpT - dist * 0.8;
    float j = (uJump > 0.5 && tj > 0.0 && tj < 0.45) ? sin(3.14159 * tj / 0.45) : 0.0;
    amp += j * aHead.z * 1.6;
    // the hands: a lateral sway of +-3 px at 1080p, each at its own pace, and a stronger bounce
    vDu = aHand * uSway * sin(uTime * (1.2 + 1.6 * fract(aHead.w * 3.0)) + aHead.w * 6.2831853) * (0.4 + 0.6 * (1.0 - uCalm));
    amp *= 1.0 + 0.6 * aHand;
    vAmp = amp * (1.0 - 0.7 * uCalm); vR = aHead.z;
    gl_Position = vec4(aHead.x * 2.0 - 1.0, 1.0 - aHead.y * 2.0, 0.0, 1.0);
    gl_PointSize = max(2.0, aHead.z * uFieldH * 2.6);
  }
`;
const fieldFragment = `
  precision highp float;
  varying float vAmp; varying float vR; varying float vDu;
  void main() {
    vec2 q = gl_PointCoord - 0.5;
    float d = length(q) * 2.0;
    float w = exp(-d * d * 3.0) * (1.0 - smoothstep(0.7, 1.0, d));
    gl_FragColor = vec4(vDu * w, vAmp * w, 0.0, 1.0);
  }
`;
// the phones: at most PHONES_MAX small lit screens, each exactly on a hand maximum of the photograph, calm passages only
const phonesVertex = `
  precision highp float;
  attribute vec4 aHead;
  uniform sampler2D uField;
  uniform vec2 uCentre, uScale, uOutPx;
  uniform float uPhones, uTime;
  varying float vOn; varying float vPx;
  void main() {
    float seed = aHead.w;
    vOn = smoothstep(0.0, 0.3, uPhones - fract(seed * 23.0) * 0.8);
    vec2 img = aHead.xy + texture2D(uField, vec2(aHead.x, 1.0 - aHead.y)).rg;
    vec2 o = (img - uCentre) / uScale + 0.5;
    gl_Position = vec4(o.x * 2.0 - 1.0, 1.0 - o.y * 2.0, 0.0, 1.0);
    vPx = uOutPx.y / 1080.0;
    gl_PointSize = 22.0 * vPx;
    if (vOn < 0.01) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  }
`;
const phonesFragment = `
  precision highp float;
  varying float vOn; varying float vPx;
  float sdRound(vec2 p, vec2 b, float r) { vec2 q = abs(p) - b + r; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r; }
  void main() {
    vec2 px = (gl_PointCoord - 0.5) * 22.0 * vPx;          // output pixels from the centre
    float d = sdRound(px, vec2(2.0, 3.5) * vPx, 0.8 * vPx);  // a 4 x 7 px screen at 1080p, rounded
    float core = 1.0 - smoothstep(-0.5, 0.5, d);
    float halo = exp(-max(d, 0.0) / (6.0 * vPx)) * 0.35;
    vec3 c = vec3(0.86, 0.93, 1.0) * (core * 1.3 + halo);
    gl_FragColor = vec4(c * vOn, 1.0);
  }
`;
// the foreground crowd: one quad per person over its atlas cell, placed by
// its head's top centre; a and b sampled together and crossfaded (the cells
// share the torso by construction)
const crowdVertex = `
  precision highp float;
  attribute vec4 aPlace;           // x (screen-height units from the left), head top y (down), shoulder width, mirror
  attribute vec4 aInfo;            // person, row, seed, state slot
  attribute vec2 aPhone;           // the phone's screen centre in cell px (pose b), x < 0: none
  uniform sampler2D uState;        // per slot: bounce up, arms, sway x, tilt
  uniform float uStateN, uSH, uTop, uAspect;
  uniform vec2 uCell;
  uniform vec3 uFg;                // the view: zoom, shift x, shift y (output uv)
  varying vec2 vCell; varying vec2 vO; varying float vArm; varying vec4 vInfo; varying vec2 vPhone;
  void main() {
    vec4 st = texture2D(uState, vec2((aInfo.w + 0.5) / uStateN, 0.5));
    float k = aPlace.z / uSH;
    vec2 cell = vec2((position.x + 0.5) * uCell.x, (0.5 - position.y) * uCell.y);
    vec2 rel = vec2(cell.x - 0.5 * uCell.x, cell.y - uTop) * k;
    rel.x *= aPlace.w;
    vec2 piv = vec2(0.0, (uCell.y * 0.8 - uTop) * k);
    float c = cos(st.a), s = sin(st.a);
    rel = piv + mat2(c, s, -s, c) * (rel - piv);
    vec2 pos = vec2(aPlace.x + st.b, aPlace.y - st.r) + rel;
    vec2 o = vec2(pos.x / uAspect, pos.y);
    o = (o - 0.5) * uFg.x + 0.5 + uFg.yz;
    vCell = cell; vO = vec2(o.x, 1.0 - o.y); vArm = st.g; vInfo = aInfo; vPhone = aPhone;
    gl_Position = vec4(o.x * 2.0 - 1.0, 1.0 - o.y * 2.0, 0.0, 1.0);
  }
`;
const crowdFragment = `
  precision highp float;
  uniform sampler2D uColor, uMask;
  uniform vec2 uCell, uGrid;
  uniform vec3 uLightL, uLightC, uLightR, uRowBody, uRowRim;
  uniform float uRimGain, uPhone, uGain, uAspect;
  uniform vec2 uSafeCenter, uSafeHalfSize; uniform float uSafeFeather;
  varying vec2 vCell; varying vec2 vO; varying float vArm; varying vec4 vInfo; varying vec2 vPhone;
  vec2 atlas(float col, float row) { vec2 uv = (vec2(col, row) * uCell + vCell) / (uGrid * uCell); return vec2(uv.x, 1.0 - uv.y); }
  float pick(vec3 v, float r) { return r < 0.5 ? v.x : (r < 1.5 ? v.y : v.z); }
  void main() {
    float p = vInfo.x, col = mod(p, uGrid.x), row = floor(p / uGrid.x);
    vec2 ua = atlas(col, row), ub = atlas(col, row + 3.0);
    vec4 ma = texture2D(uMask, ua), mb = texture2D(uMask, ub);
    float m = vArm;
    float alpha = mix(ma.r, mb.r, m);
    if (alpha < 0.004) discard;
    vec3 base = (texture2D(uColor, ua).rgb * ma.r * (1.0 - m) + texture2D(uColor, ub).rgb * mb.r * m) / max(alpha, 1e-3);
    float rim = (ma.g * (1.0 - m) + mb.g * m) / max(alpha, 1e-3);
    vec3 light = vO.x < 0.5 ? mix(uLightL, uLightC, vO.x * 2.0) : mix(uLightC, uLightR, vO.x * 2.0 - 1.0);
    float r = vInfo.y;
    vec3 c = base * pick(uRowBody, r) + base * light * 0.05 + rim * light * uRimGain * pick(uRowRim, r);
    // the phone: a small lit screen, sharp (1 px edge), in the calm
    if (vPhone.x >= 0.0) {
      vec2 d = abs(vCell - vPhone) - vec2(6.5, 11.5);
      float sd = max(d.x, d.y), px = max(fwidth(vCell.x), 1e-3);
      float on = clamp(0.5 - sd / px, 0.0, 1.0) * smoothstep(0.6, 1.0, m) * uPhone;
      c = mix(c, vec3(0.78, 0.88, 1.0) * 1.3, on);
    }
    vec2 sc = (vO * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(sc - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    alpha *= mix(0.3, 1.0, smoothstep(0.0, uSafeFeather, dz));
    gl_FragColor = vec4(c * uGain * alpha, alpha);
  }
`;
const atlasFragment = `
  precision highp float;
  uniform sampler2D uColor, uMask;
  varying vec2 vUv;
  void main() { vec3 c = texture2D(uColor, vUv).rgb; vec4 m = texture2D(uMask, vUv); gl_FragColor = vec4(mix(vec3(0.25), c, m.r) + vec3(0.0, m.g * 0.6, 0.0), 1.0); }
`;
// the occluder for the confetti: a depth-only quad with the crowd's silhouette, at a chosen depth
const occluderVertex = `
  precision highp float;
  uniform float uNdcZ;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, uNdcZ, 1.0); }
`;
const occluderFragment = `
  precision highp float;
  ${VIEW_CHUNK}
  varying vec2 vUv;
  void main() {
    vec2 o = vec2(vUv.x, 1.0 - vUv.y);
    vec2 img = viewImg(o);
    float crowd = texture2D(uMasks, flipV(img)).a;
    if (crowd < 0.5) discard;
    gl_FragColor = vec4(0.0);
  }
`;

function noiseTexture() {
  const S = 128, d = new Uint8Array(S * S * 4);
  const rnd = mulberry(99);
  const g = (n) => { const a = new Float32Array(n * n); for (let i = 0; i < n * n; i++) a[i] = rnd(); return a; };
  const oct = [[8, g(8)], [16, g(16)], [32, g(32)]];
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    let v = 0, w = 0.55;
    for (const [n, a] of oct) {
      const fx = x / S * n, fy = y / S * n, ix = Math.floor(fx), iy = Math.floor(fy), tx = fx - ix, ty = fy - iy;
      const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
      const at = (i, j) => a[((j % n + n) % n) * n + ((i % n + n) % n)];
      v += w * ((at(ix, iy) * (1 - sx) + at(ix + 1, iy) * sx) * (1 - sy) + (at(ix, iy + 1) * (1 - sx) + at(ix + 1, iy + 1) * sx) * sy);
      w *= 0.5;
    }
    const o = (y * S + x) * 4; d[o] = d[o + 1] = d[o + 2] = Math.round(Math.min(1, v / 0.96) * 255); d[o + 3] = 255;
  }
  const t = new THREE.DataTexture(d, S, S); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.magFilter = t.minFilter = THREE.LinearFilter; t.needsUpdate = true;
  return t;
}

// --- the visualizer ----------------------------------------------------------------------
// config: { id, scene, program, palettes, screens, shots, intensityCap, protectSky, tiers }
export class PhotoStage extends Visualizer {
  constructor(config, options) {
    super();
    const o = options || {};
    this.cfg = config;
    this._tier = o.qualityTier != null ? o.qualityTier : 2;
    const tiers = config.tiers || { 0: {}, 1: {}, 2: {} };
    this._T = Object.assign({ lasers: this._tier >= 1 ? (this._tier === 2 ? 16 : 8) : 0, confetti: this._tier === 2 ? 2500 : (this._tier === 1 ? 400 : 0), parallax: this._tier > 0, beams: this._tier === 2 ? 32 : (this._tier === 1 ? 20 : 12), shots: this._tier > 0 ? Object.keys(SHOTS) : ['wide', 'dj', 'crowd'] }, tiers[this._tier] || {});
    this._post = new VdjPost({ bloomStrength: 0.10, bloomThreshold: 0.80, grain: 0.015, vignette: 0.25 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 0 });
    this._rng = mulberry(0xC0FFEE ^ (config.id.length * 977));
    this._forcedShot = urlParam('stageshot'); if (!SHOTS[this._forcedShot]) this._forcedShot = null;
    this._dbg = urlParam('stagelayers') === '1' ? 1 : (urlParam('stagemasks') === '1' ? 2 : (urlParam('stageheads') === '1' ? 3 : 0));
    const fs = urlParam('stagescreens'); this._forcedScreens = { content: 0, cover: 1, title: 2, artist: 3 }[fs]; if (this._forcedScreens === undefined) this._forcedScreens = null;
    this._dbgDrop = urlParam('stagedrop') === '1';
    this._decisions = [];
    this._ready = false; this._unavailable = false;
  }
  get postProcessing() { return this._post.value; }
  _say(what) { this._decisions.push({ t: +this._time.toFixed(2), what }); if (this._decisions.length > 40) this._decisions.shift(); }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    this._time = 0; this._beatCount = 0; this._barCount = 0; this._lastBeatAt = -10; this._lastBarAt = -10; this._beatPeriod = 60 / 126;
    this._build = 0; this._lastDropKey = null; this._dropAt = -100; this._flash = 0; this._lumSince = 0; this._jumpAt = -10;
    this._coverUrl = undefined; this._cover = null; this._coverPending = null; this._coverAvg = new THREE.Color(0.3, 0.3, 0.3); this._title = '';
    this._program = PROGRAMS[0]; this._palette = this._makePalette(0); this._palIndex = 0;
    this._shot = 'wide'; this._shotSince = 0; this._shotBars = 2; this._view = { c: new THREE.Vector2(0.5, 0.5), z: 1, tc: new THREE.Vector2(0.5, 0.5), tz: 1 };
    this._screenModes = [3, 3, 3, 3]; this._sideMap = 2; this._generator = 'tunnel'; this._coverShown = false; this._peakDone = false; this._layout = 0; this._lastLayout = -1; this._layoutAt = 0; this._bg = 0; this._content = 'artist'; this._cycleStart = 0; this._programAt = 0;
    this._arms = 0; this._phonesLvl = 0; this._co2 = []; this._blinder = 0; this._flameGlow = 0; this._laserLevel = 0; this._sheet = 0; this._strobe = 0;
    this._size = new THREE.Vector2(); this._aspect = 16 / 9; this._zoomCap = 1;
    const safe = { uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._noise = noiseTexture();
    const [CW, CH] = CONTENT_RES[this._tier] || CONTENT_RES[2];
    // the title and the countdown digits: geometry-rendered glyphs into MSAA targets (R21), redrawn when the text changes
    const textRT = () => new THREE.WebGLRenderTarget(TITLE_W, TITLE_H, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: true, samples: this._tier > 0 ? 4 : 2 });
    this._rtTitle = textRT(); this._rtDigits = textRT(); this._rtArtist = textRT(); this._titleDirty = true; this._digitsDirty = true; this._artistDirty = true; this._title = ''; this._artist = ''; this._artistSpan = [0.03, 0.97]; this._letterBounds = [];
    this._rtStack = new THREE.WebGLRenderTarget(STACK_W, STACK_H, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: true, samples: this._tier > 0 ? 4 : 2 });
    this._glyphMat = new THREE.ShaderMaterial({ uniforms: { uPlace: { value: new THREE.Vector4(0, 0, 1, 1) } }, vertexShader: 'precision highp float; uniform vec4 uPlace; void main() { gl_Position = vec4(uPlace.xy + position.xy * uPlace.zw, 0.0, 1.0); }', fragmentShader: 'precision highp float; void main() { gl_FragColor = vec4(1.0); }', depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    this._glyphScene = new THREE.Scene();
    loadFont().then((f) => { this._font = f; this._titleDirty = true; this._digitsDirty = true; });
    loadFont(FONT_BOLD).then((f) => { this._fontBold = f || this._font; this._artistDirty = true; });
    // the content generators (Mixdeck's, plus the stage's spectrum and shapes)
    this._bus = {
      uRes: { value: new THREE.Vector2(CW, CH) }, uAspect: { value: CW / CH }, uTime: { value: 0 }, uBeats: { value: 0 }, uBeatPhase: { value: 0 }, uBarPhase: { value: 0 },
      uPulse: { value: 0 }, uBarPulse: { value: 0 }, uEnergy: { value: 0.5 }, uSeed: { value: 0.3 }, uBarIndex: { value: 0 }, uBands: { value: new THREE.Vector3(0.5, 0.4, 0.4) },
      uCol0: { value: new THREE.Color(0xff3c8c) }, uCol1: { value: new THREE.Color(0x3cc8ff) }, uCol2: { value: new THREE.Color(0xffd23c) }, uCover: { value: null }, uHasCover: { value: 0 }
    };
    const frags = { tunnel: MIXDECK_BG.tunnel, plasma: MIXDECK_BG.plasma, rays: MIXDECK_BG.rays, nebula: MIXDECK_BG.nebula, spectrum: STAGE_SCREEN_FRAGMENTS.spectrum, shapes: STAGE_SCREEN_FRAGMENTS.shapes };
    this._gens = {};
    for (const k in frags) this._gens[k] = new THREE.ShaderMaterial({ uniforms: this._bus, vertexShader: MIXDECK_QUAD, fragmentShader: frags[k], depthTest: false, depthWrite: false });
    this._genNames = Object.keys(frags).filter((k) => !(this.cfg.screens && this.cfg.screens.exclude && this.cfg.screens.exclude.includes(k)));
    this._passScene = new THREE.Scene(); this._passQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._gens.tunnel); this._passQuad.frustumCulled = false; this._passScene.add(this._passQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    const rt = (w, h, opts) => new THREE.WebGLRenderTarget(w, h, Object.assign({ depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter }, opts || {}));
    this._rtA = rt(CW, CH, { generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter }); this._rtB = rt(CW, CH, { generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter });
    this._bus.uRes.value.set(CW, CH); this._bus.uAspect.value = CW / CH;
    this._rtAvg = rt(2, 1, {});
    this._rtBeams = rt(640, 360, { type: THREE.HalfFloatType });
    this._rtField = rt(256, 144, { type: THREE.HalfFloatType });
    // the view uniforms, shared by every pass
    this._viewU = {
      uDepth: { value: null }, uField: { value: this._rtField.texture }, uMasks: { value: null }, uCentre: { value: this._view.c }, uScale: { value: new THREE.Vector2(1, 1) },
      uParallax: { value: new THREE.Vector2(0, 0) }, uProxRef: { value: 0.5 }, uNod: { value: 0 }, uProxCrowd: { value: 0.3 }, uBand: { value: Array.from({ length: BANDS }, () => new THREE.Vector2()) }
    };
    const mat3s = []; for (let k = 0; k < MAX_SCREENS; k++) mat3s.push(new THREE.Matrix3());
    this._u = Object.assign({
      uDark: { value: null }, uLayers: { value: null }, uWash: { value: null }, uMasksId: { value: null }, uBeams: { value: this._rtBeams.texture }, uContentA: { value: this._rtA.texture }, uContentB: { value: this._rtB.texture },
      uCover: { value: null }, uTitle: { value: this._rtTitle.texture }, uArtist: { value: this._rtArtist.texture }, uStack: { value: this._rtStack.texture }, uStackAsp: { value: STACK_W / STACK_H }, uDigits: { value: this._rtDigits.texture }, uSMax: { value: 1 }, uWMax: { value: 1 }, uHasCover: { value: 0 }, uCoverAsp: { value: 1 }, uTitleAsp: { value: TITLE_W / TITLE_H }, uCount: { value: 0 },
      uTime: { value: 0 }, uFlash: { value: 0 }, uGain: { value: 1 }, uAspect: { value: 16 / 9 }, uDbg: { value: this._dbg }, uSky: { value: this.cfg.protectSky ? 1 : 0 }, uProxSky: { value: 0.1 },
      uGroupCol: { value: [0, 1, 2, 3].map(() => new THREE.Color(0.2, 0.2, 0.2)) }, uGroupGain: { value: [1, 1, 1, 1] },
      uZoneCol: { value: [0, 1, 2, 3, 4, 5].map(() => new THREE.Color(1, 1, 1)) }, uZoneLvl: { value: [0, 0, 0, 0, 0, 0] },
      uScreenH: { value: mat3s }, uScreenInfo: { value: Array.from({ length: MAX_SCREENS }, () => new THREE.Vector4(0, 0, 0, 1)) }, uScreenN: { value: 0 }, uSideMap: { value: 0 }, uScreenBright: { value: 1 }, uScreenFlash: { value: 0 }, uFlameGlow: { value: 0 },
      uLayout: { value: 0 }, uBg: { value: 0 }, uRevealX: { value: 1 }, uArtistSpan: { value: new THREE.Vector2(0.03, 0.97) }, uTitleSpan: { value: new THREE.Vector2(0.03, 0.97) }, uKick: { value: 0 }, uScroll: { value: 0 }, uFillPhase: { value: 0 }, uRowAsp: { value: 4 }, uTextCol: { value: new THREE.Color(1, 1, 1) },
      uCrowdRim: { value: 0.5 }, uDjRim: { value: 0.6 }, uImgPx: { value: new THREE.Vector2(1365, 768) }, uOutPx: { value: new THREE.Vector2(1920, 1080) }, uSharp: { value: 0 }
    }, this._viewU, safe);
    this._mat = new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: quadVertex, fragmentShader: compositeFragment, depthTest: false, depthWrite: false, extensions: { derivatives: true } });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat); this._quad.frustumCulled = false; this._quad.renderOrder = -1000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);
    // the beams pass
    this._beamU = Object.assign({
      uWash: { value: null }, uNoise: { value: this._noise }, uO: { value: Array.from({ length: MAX_BEAMS }, () => new THREE.Vector2()) }, uD: { value: Array.from({ length: MAX_BEAMS }, () => new THREE.Vector2(0, 1)) },
      uC: { value: Array.from({ length: MAX_BEAMS }, () => new THREE.Vector4(1, 1, 1, 0)) }, uSpread: { value: new Float32Array(MAX_BEAMS).fill(0.05) }, uN: { value: 0 }, uTime: { value: 0 }, uHaze: { value: 0.5 }, uAspect: { value: 16 / 9 },
      uBeamProx: { value: 0.3 }, uSky: { value: this.cfg.protectSky ? 1 : 0 }, uProxSky: { value: 0.1 }, uLaser: { value: Array.from({ length: MAX_LASERS }, () => new THREE.Vector4()) }, uLaserCol: { value: new THREE.Color(0.2, 1, 0.4) }, uLaserN: { value: 0 }, uSheet: { value: 0 }, uSheetY: { value: 0.7 },
      uCo2: { value: Array.from({ length: MAX_FX }, () => new THREE.Vector4(0, 0, -1, 0)) }, uCo2N: { value: 0 }, uOutPx: { value: new THREE.Vector2(1920, 1080) }
    }, this._viewU);
    this._beamMat = new THREE.ShaderMaterial({ uniforms: this._beamU, vertexShader: quadVertex, fragmentShader: beamsFragment, depthTest: false, depthWrite: false });
    // the bounce field
    this._fieldU = { uBeatPhase: { value: 0 }, uBeats: { value: 0 }, uAmp: { value: 0.35 }, uJump: { value: 0 }, uJumpT: { value: 0 }, uCalm: { value: 0 }, uFieldH: { value: 144 }, uAspect: { value: 16 / 9 }, uTime: { value: 0 }, uSway: { value: 4 / 1080 } };
    this._fieldMat = new THREE.ShaderMaterial({ uniforms: this._fieldU, vertexShader: fieldVertex, fragmentShader: fieldFragment, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending, transparent: true });
    this._fieldScene = new THREE.Scene();
    // the phones (calm passages), on the hands of the photograph
    this._phonesU = Object.assign({ uPhones: { value: 0 }, uTime: { value: 0 }, uOutPx: this._u.uOutPx }, { uField: this._viewU.uField, uCentre: this._viewU.uCentre, uScale: this._viewU.uScale });
    this._phonesMat = new THREE.ShaderMaterial({ uniforms: this._phonesU, vertexShader: phonesVertex, fragmentShader: phonesFragment, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending });
    // the confetti occluder
    this._occU = Object.assign({ uNdcZ: { value: 0.5 } }, this._viewU);
    this._occMat = new THREE.ShaderMaterial({ uniforms: this._occU, vertexShader: occluderVertex, fragmentShader: occluderFragment, colorWrite: false, depthWrite: true, depthTest: true });
    // the particle layers
    this._pyro = (this.cfg.program.flames || this.cfg.program.co2) && this._tier > 0 ? new PyroLayer({ max: this._tier === 2 ? 5000 : 2500, trails: this._tier === 2, smoke: false, flames: true, crackle: false, gain: 0.8, renderOrder: 20 }) : null;
    if (this._pyro) { this._pyro.init(scene); this._pyro.setZoom(0.5, 0, -0.35); }   // Pyro's stage is the whole frame: its jets scaled to a quarter of the stage's height
    // the ground far below the frame: a piece never lies still in view; the layer is hidden between shots (two bars + the fall)
    this._confetti = this.cfg.program.confetti && this._T.confetti > 0 ? new ConfettiLayer({ max: this._T.confetti, ribbons: this._tier === 2, unit: 1.0, groundY: -60, fog: 0, ambient: 0.02, depthTest: true, renderOrder: 10 }) : null;
    this._confettiUntil = -1;
    if (this._confetti) { this._confetti.init(scene); if (this.cfg.program.confetti !== 'palette') this._confetti.setPalette(this.cfg.program.confetti); this._confettiVisible(false); }
    // the foreground crowd
    this._fg = null; this._fgRows = Math.max(0, Math.min(3, parseInt(urlParam('crowdrows') || '3', 10))); if (!(this._fgRows >= 0)) this._fgRows = 3;
    this._fgPose = urlParam('crowdpose'); this._fgAtlas = urlParam('crowdatlas') === '1';
    if (this._fgRows > 0 || this._fgAtlas) loadCrowd().then((c) => { if (c && this._scene) this._initFg(c); });
    // the assets
    loadScene(this.cfg.scene).then((a) => { if (this._scene) this._onAssets(a); else this._disposeAssets(a); }).catch((e) => { this._unavailable = true; console.warn('[photostage] ' + this.cfg.id + ' indisponible : ' + (e && e.message ? e.message : e)); });
  }
  _onAssets(a) {
    this._assets = a;
    const info = a.info;
    this._u.uDark.value = a.dark; this._u.uLayers.value = a.layers; this._u.uWash.value = a.wash; this._u.uMasksId.value = a.masksId; this._beamU.uWash.value = a.wash;
    this._viewU.uDepth.value = a.depth; this._viewU.uMasks.value = a.masks;
    this._u.uSMax.value = info.sMax; this._u.uWMax.value = info.wMax; this._u.uImgPx.value.set(info.size[0], info.size[1]);
    this._u.uProxSky.value = info.proxSky; this._beamU.uProxSky.value = info.proxSky; this._beamU.uBeamProx.value = info.proxCrowd * 0.95; this._viewU.uProxRef.value = (info.proxCrowd + info.proxSky) * 0.5; this._viewU.uProxCrowd.value = info.proxCrowd;
    this._srcAspect = info.size[0] / info.size[1];
    console.info('[photostage] ' + this.cfg.id + ' : source ' + info.size[0] + 'x' + info.size[1] + ' px (' + (info.warnings || []).join('; ') + '), ' + info.screens.length + ' écrans, ' + (a.heads.length / 4) + ' têtes');
    // the screens: homography (pixels -> unit square) and group / span
    const scr = info.screens.slice(0, MAX_SCREENS);
    this._u.uScreenN.value = scr.length;
    const xs = scr.map((s) => s.centre[0]); const xMin = Math.min(...xs), xMax = Math.max(...xs);
    // ONE image across the whole row of screens: each screen shows its horizontal slice of the content, by its extent over the row (left edge of the leftmost to right edge of the rightmost)
    const rowMin = Math.min(...scr.map((sc) => Math.min(...sc.quad.map((q) => q[0])))), rowMax = Math.max(...scr.map((sc) => Math.max(...sc.quad.map((q) => q[0]))));
    const rowW = Math.max(1e-3, rowMax - rowMin);
    const cRow = scr.find((sc) => sc.group === 1) || scr[0]; const rowH = Math.max(1e-3, Math.max(...cRow.quad.map((q) => q[1])) - Math.min(...cRow.quad.map((q) => q[1])));
    this._u.uRowAsp.value = (rowW * this._srcAspect) / rowH;
    this._hasVertical = scr.some((sc) => { const w = Math.max(...sc.quad.map((q) => q[0])) - Math.min(...sc.quad.map((q) => q[0])), h = Math.max(...sc.quad.map((q) => q[1])) - Math.min(...sc.quad.map((q) => q[1])); return (w * this._srcAspect) / Math.max(1e-3, h) < 0.8; });
    for (let k = 0; k < scr.length; k++) {
      const h = scr[k].h; this._u.uScreenH.value[k].set(h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], h[8]);
      const x0 = (Math.min(...scr[k].quad.map((q) => q[0])) - rowMin) / rowW, x1 = (Math.max(...scr[k].quad.map((q) => q[0])) - rowMin) / rowW;
      // side screens are flagged by a span outside the centre's (the shader's mirror / different modes key on it)
      this._u.uScreenInfo.value[k].set(scr[k].group, 0, scr[k].group === 1 ? x0 : (x0 < 0.5 ? x0 - 2.0 : x0 + 2.0), scr[k].group === 1 ? x1 : (x0 < 0.5 ? x1 - 2.0 : x1 + 2.0));
    }
    this._dj = this._djFromMask(info);
    // the heads: an instanced attribute for the field and the arms
    const heads = a.heads, N = heads.length / 4;
    const geoF = new THREE.BufferGeometry(); geoF.setAttribute('aHead', new THREE.BufferAttribute(heads, 4)); geoF.setAttribute('position', new THREE.BufferAttribute(new Float32Array(N * 3), 3));
    this._fieldPts = new THREE.Points(geoF, this._fieldMat); this._fieldPts.frustumCulled = false; this._fieldScene.add(this._fieldPts);
    // the hands: the maxima at the top of the crowd (the raised arms of the photograph); they sway, and carry the phones
    const topY = info.crowdTopMedian != null ? info.crowdTopMedian : info.lipY + 0.03;
    const hand = new Float32Array(N); const handIdx = [];
    for (let i = 0; i < N; i++) if (heads[i * 4 + 1] < topY + 0.012) { hand[i] = 1; handIdx.push(i); }
    geoF.setAttribute('aHand', new THREE.BufferAttribute(hand, 1));
    // the phones: PHONES_MAX hands spread across the width (every k-th by x)
    handIdx.sort((p, q) => heads[p * 4] - heads[q * 4]);
    const step = Math.max(1, Math.floor(handIdx.length / PHONES_MAX));
    const ph = []; for (let k = 0; k < handIdx.length && ph.length < PHONES_MAX; k += step) ph.push(handIdx[k]);
    const phArr = new Float32Array(ph.length * 4); ph.forEach((i, k) => phArr.set(heads.subarray(i * 4, i * 4 + 4), k * 4));
    const geoP = new THREE.BufferGeometry(); geoP.setAttribute('aHead', new THREE.BufferAttribute(phArr, 4)); geoP.setAttribute('position', new THREE.BufferAttribute(new Float32Array(ph.length * 3), 3)); geoP.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._phones = new THREE.Points(geoP, this._phonesMat); this._phones.frustumCulled = false; this._phones.renderOrder = -900; this._scene.add(this._phones);
    this._handCount = handIdx.length;
    this._occ = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._occMat); this._occ.frustumCulled = false; this._occ.renderOrder = 5; if (this._confetti) this._scene.add(this._occ);
    this._lenses = info.lenses; this._fx = info.fx.slice(0, MAX_FX); this._lipY = info.lipY;
    this._beamU.uSheetY.value = info.lipY - 0.045;   // the sheets pass above the crowd's top edge with the 2 % margin
    this._applyScreenModes();
    this._ready = true;
    this._say('assets ' + this.cfg.scene + ' ' + info.hash + ' : ' + scr.length + ' écrans, ' + N + ' têtes (' + this._handCount + ' mains), ' + info.lenses.length + ' lentilles');
    this._pickShot('wide', 2);
  }
  _djFromMask(info) {
    // the DJ's head, measured by the build (top rows of the hole in the centre screen); the screen's bottom centre as a fallback
    if (info.dj) return { x: info.dj.x, y: info.dj.y, r: Math.max(0.006, info.dj.r) };
    const c = info.screens.find((s) => s.group === 1) || info.screens[0];
    const xs = c.quad.map((q) => q[0]), ys = c.quad.map((q) => q[1]);
    return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: Math.max(...ys) - 0.04, r: 0.009 };
  }
  _disposeAssets(a) { for (const k of ['dark', 'layers', 'wash', 'masks', 'masksId', 'depth']) if (a[k]) a[k].dispose(); }

  // --- the foreground crowd ----------------------------------------------------------------
  _initFg(c) {
    const m = c.m;
    const geo = new THREE.InstancedBufferGeometry();
    const plane = new THREE.PlaneGeometry(1, 1);
    geo.index = plane.index; geo.setAttribute('position', plane.getAttribute('position'));
    const place = new THREE.InstancedBufferAttribute(new Float32Array(CROWD_MAX * 4), 4), inf = new THREE.InstancedBufferAttribute(new Float32Array(CROWD_MAX * 4), 4), phone = new THREE.InstancedBufferAttribute(new Float32Array(CROWD_MAX * 2), 2);
    geo.setAttribute('aPlace', place); geo.setAttribute('aInfo', inf); geo.setAttribute('aPhone', phone);
    geo.instanceCount = 0;
    const state = new Float32Array(CROWD_MAX * 4);
    const stateTex = new THREE.DataTexture(state, CROWD_MAX, 1, THREE.RGBAFormat, THREE.FloatType);
    stateTex.minFilter = stateTex.magFilter = THREE.NearestFilter; stateTex.generateMipmaps = false; stateTex.needsUpdate = true;
    const u = {
      uState: { value: stateTex }, uStateN: { value: CROWD_MAX }, uSH: { value: m.shoulder }, uTop: { value: m.top }, uCell: { value: new THREE.Vector2(m.cell[0], m.cell[1]) }, uGrid: { value: new THREE.Vector2(m.cols, m.rows) },
      uAspect: { value: this._aspect }, uFg: { value: new THREE.Vector3(1, 0, 0) }, uColor: { value: c.color }, uMask: { value: c.mask },
      uLightL: { value: new THREE.Color(0.3, 0.3, 0.4) }, uLightC: { value: new THREE.Color(0.3, 0.3, 0.4) }, uLightR: { value: new THREE.Color(0.3, 0.3, 0.4) },
      uRowBody: { value: new THREE.Vector3(...CROWD_ROWS.map((r) => r.body)) }, uRowRim: { value: new THREE.Vector3(...CROWD_ROWS.map((r) => r.rim)) },
      uRimGain: { value: 1 }, uPhone: { value: 0 }, uGain: { value: 1 },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    const mat = new THREE.ShaderMaterial({ uniforms: u, vertexShader: crowdVertex, fragmentShader: crowdFragment, side: THREE.DoubleSide, transparent: true, depthTest: false, depthWrite: false, blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor, extensions: { derivatives: true } });
    const mesh = new THREE.Mesh(geo, mat); mesh.frustumCulled = false; mesh.renderOrder = 30;
    this._scene.add(mesh);
    this._fg = { m, mesh, geo, mat, u, place, inf, phone, state, stateTex, slots: [], aspect: 0, rng: mulberry(0xF00D ^ (this.cfg.id.length * 131)) };
    if (this._fgAtlas) {
      const am = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: { uColor: { value: c.color }, uMask: { value: c.mask } }, vertexShader: quadVertex, fragmentShader: atlasFragment, depthTest: false, depthWrite: false }));
      am.frustumCulled = false; am.renderOrder = 40; this._scene.add(am); this._fg.atlasMesh = am; mesh.visible = false;
    }
    this._say('foule : ' + m.persons.length + ' personnes (' + m.hash + ')');
  }
  // the rows: back to front, overlapping, never the same person next to itself (in any row), a size jitter of +-8 %, a random mirror
  _layoutFg() {
    const F = this._fg, asp = this._aspect, rng = F.rng, P = F.m.persons.length;
    const slots = [];
    CROWD_ROWS.forEach((R, ri) => {
      if (2 - ri >= this._fgRows) return;          // ?crowdrows: the front rows first
      const n = R.n[0] + Math.floor(rng() * (R.n[1] - R.n[0] + 1));
      const span = asp + R.sh * 0.6, step = span / n;
      for (let i = 0; i < n; i++) {
        const x = -R.sh * 0.3 + (i + 0.5) * step + (rng() - 0.5) * step * 0.45;
        const sh = R.sh * (0.92 + 0.16 * rng());
        const y = R.y + (rng() - 0.5) * 0.012 * (ri + 1) / 2;
        let person = -1;
        for (let tries = 0; tries < 60 && person < 0; tries++) {
          const cand = Math.floor(rng() * P);
          if (slots.some((o) => o.person === cand && Math.abs(o.x - x) < 0.75 * (o.sh + sh) * 1.6)) continue;
          person = cand;
        }
        if (person < 0) person = Math.floor(rng() * P);
        slots.push({ x, y, sh, mirror: rng() < 0.5 ? -1 : 1, person, row: ri, seed: rng(), phase: rng() * 0.22, amp: 0.7 + 0.6 * rng(), swayW: 0.5 + 0.6 * rng(), swayP: rng() * 6.283, u: rng(), nextRoll: 0, arm: 0, armT: 0 });
      }
    });
    const n = Math.min(CROWD_MAX, slots.length);
    for (let i = 0; i < n; i++) {
      const s = slots[i];
      F.place.array.set([s.x, s.y, s.sh, s.mirror], i * 4);
      F.inf.array.set([s.person, s.row, s.seed, i], i * 4);
      const ph = F.m.phones && F.m.phones[s.person];
      F.phone.array.set(ph ? [ph[0], ph[1]] : [-1, -1], i * 2);
    }
    F.place.needsUpdate = true; F.inf.needsUpdate = true; F.phone.needsUpdate = true;
    F.geo.instanceCount = n; F.slots = slots.slice(0, n); F.aspect = asp;
    // the check: the same person overlapping or touching itself, any rows
    F.dupes = 0;
    for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) if (slots[i].person === slots[j].person && Math.abs(slots[i].x - slots[j].x) < 0.75 * (slots[i].sh + slots[j].sh) * 1.6) F.dupes++;
  }
  // per frame: the bounce (fast up, slow down) on each beat with its own phase, the arms (a share raised, each person rerolling every 1-2 bars), the drop's jump wave, the sway
  _updateFg(a, bp, intensity, calm, dropHot, dt) {
    const F = this._fg; if (!F) return;
    if (Math.abs(F.aspect - this._aspect) > 1e-3) this._layoutFg();
    const bar = this._beatPeriod * 4, t = this._time;
    const share = this._fgPose === 'b' ? 1 : (this._fgPose === 'a' ? 0 : (dropHot > 0.01 && this._time - this._dropAt < this._beatPeriod * 4 ? 1 : (calm > 0.5 ? 0.1 : Math.max(0.3 + 0.2 * clamp01((intensity - 0.5) * 2), 0.3 + 0.6 * this._build))));
    const pulse = Math.max(0.3, a.pulse) * (1 - 0.7 * calm);
    const sinceDrop = t - this._dropAt, jumping = sinceDrop >= 0 && sinceDrop < this._beatPeriod * 4.5;
    for (let i = 0; i < F.slots.length; i++) {
      const s = F.slots[i], R = CROWD_ROWS[s.row];
      // the arms: reroll the person's draw every 1-2 bars (4-8 in the calm); up when the draw is under the share
      if (t >= s.nextRoll) { s.u = F.rng(); s.nextRoll = t + bar * (calm > 0.5 ? 4 + 4 * F.rng() : 1 + F.rng()); }
      const up = s.u < share ? 1 : 0;
      s.arm += (up - s.arm) * Math.min(1, dt / 0.06);
      // the bounce
      const x = ((bp - s.phase) % 1 + 1) % 1;
      const shape = x < 0.18 ? smooth(x / 0.18) : Math.pow(1 - (x - 0.18) / 0.82, 2);
      let dy = R.bounce * s.amp * shape * pulse * (0.6 + 0.4 * intensity);
      // the drop: four jumps, a wave from the centre (a quarter beat to the edges)
      if (jumping) {
        const delay = Math.abs(s.x / this._aspect - 0.5) * 0.5 * this._beatPeriod;
        const tj = sinceDrop - delay;
        if (tj > 0 && tj < this._beatPeriod * 4) { const ph = (tj / this._beatPeriod) % 1; dy = Math.max(dy, R.jump * Math.sin(Math.PI * ph)); }
      }
      // the front row's heads stay under 85 % of the height, except in the drop's jump (the overlay keeps its centre)
      if (s.row === 2 && !jumping) dy = Math.min(dy, Math.max(0, s.y - 0.85));
      const sway = 0.005 * this._aspect * Math.sin(t * 0.9 * s.swayW + s.swayP);
      const tilt = (2 * Math.PI / 180) * Math.sin(t * 0.7 * s.swayW + s.swayP * 1.7);
      F.state.set([dy, s.arm, sway, tilt], i * 4);
    }
    F.stateTex.needsUpdate = true;
    // the light on the rims: the top zones by x, the centre screens, the flashes
    const zc = this._u.uZoneCol.value, zl = this._u.uZoneLvl.value, gc = this._u.uGroupCol.value[1], gg = this._u.uGroupGain.value[1];
    const white = 1.5 * this._blinder + 2.0 * this._u.uFlash.value + 0.8 * this._u.uScreenFlash.value;
    [F.u.uLightL.value, F.u.uLightC.value, F.u.uLightR.value].forEach((L, k) => {
      const z = 0.35 * Math.min(2.5, zl[k]), g = 0.3 * gg;
      L.setRGB(zc[k].r * z + gc.r * g + white, zc[k].g * z + gc.g * g + white, zc[k].b * z + gc.b * g + white);
    });
    F.u.uRimGain.value = 0.9 + 0.5 * dropHot;
    F.u.uPhone.value = this._phonesLvl / Math.max(0.01, this.cfg.program.phones != null ? this.cfg.program.phones : 0.6);
    F.u.uGain.value = this._u.uGain.value;
    F.u.uAspect.value = this._aspect;
    // the view: the zoom about the centre, and the strongest parallax (the photograph's at proximity 1.35)
    const sc = this._viewU.uScale.value, par = this._viewU.uParallax.value, k = 1.35 - this._viewU.uProxRef.value;
    F.u.uFg.value.set(this._view.z, -k * par.x / Math.max(1e-3, sc.x), -k * par.y / Math.max(1e-3, sc.y));
  }

  // --- palettes, title, cover ----------------------------------------------------------------
  _makePalette(i) {
    const P = this.cfg.palettes; const set = P[i % P.length];
    return set.map((hex) => vdjColor(hex));
  }
  _setTitle(text) { this._title = text; this._titleDirty = true; }
  _setArtist(text) { this._artist = text; this._artistDirty = true; }
  // text -> a target, the glyphs as ShapeGeometry (never a raster font): fitted to 94 % of the width, cap height 70 % of the height at most
  _drawText(renderer, rt, text, font, span) {
    font = font || this._font;
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(rt); renderer.setClearColor(0x000000, 1); renderer.clear(true, false, false);
    const str = String(text || '').toUpperCase().slice(0, 40);
    if (font && str.trim()) {
      const shapes = font.generateShapes(str, 1);
      const g = new THREE.ShapeGeometry(shapes, 8); g.computeBoundingBox();
      const bb = g.boundingBox, w = Math.max(1e-3, bb.max.x - bb.min.x);
      const capH = (font.data.capHeight || 700) / font.data.resolution;
      // scale: fit the width (clip units: 2 wide), cap the height (0.7 of 2 tall)
      const sx = 1.88 / w, sy = 1.4 / capH, sc = Math.min(sx, sy);
      if (span) { span[0] = 0.5 - sc * w / 4; span[1] = 0.5 + sc * w / 4; }
      const asp = TITLE_W / TITLE_H;
      const m = new THREE.Mesh(g, this._glyphMat);
      this._glyphMat.uniforms.uPlace.value.set(-(bb.min.x + w / 2) * sc, -(capH / 2) * sc * asp, sc, sc * asp);
      m.frustumCulled = false; this._glyphScene.add(m);
      renderer.render(this._glyphScene, this._ortho);
      this._glyphScene.remove(m); g.dispose();
    }
    renderer.setRenderTarget(prev);
  }
  // the name stacked letter by letter in a tall target (the vertical screens)
  _drawStacked(renderer, text, font) {
    font = font || this._fontBold || this._font;
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this._rtStack); renderer.setClearColor(0x000000, 1); renderer.clear(true, false, false);
    const str = String(text || '').toUpperCase().replace(/\s+/g, '').slice(0, 14);
    if (font && str) {
      const n = str.length, capH = (font.data.capHeight || 700) / font.data.resolution, asp = STACK_W / STACK_H;
      const rowH = 2 / n;                                 // clip height per letter
      const sc = Math.min(0.78 * rowH / capH, 1.7 / (capH * 1.3));   // cap height = 78 % of a row, letters never wider than the target
      for (let i = 0; i < n; i++) {
        const g = new THREE.ShapeGeometry(font.generateShapes(str[i], 1), 8); g.computeBoundingBox();
        const bb = g.boundingBox, w = Math.max(1e-3, bb.max.x - bb.min.x);
        const scx = Math.min(sc / asp, 1.8 / w);
        const m = new THREE.Mesh(g, this._glyphMat);
        this._glyphMat.uniforms.uPlace.value.set(-(bb.min.x + w / 2) * scx, 1 - (i + 0.5) * rowH - (capH / 2) * sc, scx, sc);
        m.frustumCulled = false; this._glyphScene.add(m); renderer.render(this._glyphScene, this._ortho); this._glyphScene.remove(m); g.dispose();
      }
    }
    renderer.setRenderTarget(prev);
  }
  // where each letter ends, in the artist target's x (for the LETTERS layout)
  _computeLetterBounds(text, font) {
    const str = String(text || '').toUpperCase().slice(0, 40); const out = [];
    if (!font || !str.trim()) { this._letterBounds = out; return; }
    const gl = font.data.glyphs; let total = 0; const cum = [];
    for (const ch of str) { const g = gl[ch] || gl['?']; total += g ? g.ha : 600; cum.push(total); }
    const [a, b] = this._artistSpan;
    for (const c of cum) out.push(a + (b - a) * c / Math.max(1, total));
    this._letterBounds = out;
  }
  _drawDigits(renderer) {
    // the four digits side by side in one target (x quarter per digit)
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this._rtDigits); renderer.setClearColor(0x000000, 1); renderer.clear(true, false, false);
    if (this._font) for (let i = 0; i < 4; i++) {
      const g = new THREE.ShapeGeometry(this._font.generateShapes(String(i + 1), 1), 8); g.computeBoundingBox();
      const bb = g.boundingBox, w = Math.max(1e-3, bb.max.x - bb.min.x), capH = (this._font.data.capHeight || 700) / this._font.data.resolution;
      const asp = TITLE_W / TITLE_H, sc = 1.5 / capH;
      const m = new THREE.Mesh(g, this._glyphMat);
      this._glyphMat.uniforms.uPlace.value.set(-1 + (i + 0.5) * 0.5 - (bb.min.x + w / 2) * sc / asp, -(capH / 2) * sc, sc / asp, sc);
      m.frustumCulled = false; this._glyphScene.add(m); renderer.render(this._glyphScene, this._ortho); this._glyphScene.remove(m); g.dispose();
    }
    renderer.setRenderTarget(prev);
  }
  _loadCover(url) {
    this._coverUrl = url;
    if (!url) { if (this._cover) this._cover.dispose(); this._cover = null; this._u.uCover.value = null; this._u.uHasCover.value = 0; this._bus.uCover.value = null; this._bus.uHasCover.value = 0; return; }
    const token = {}; this._coverPending = token;
    new THREE.TextureLoader().setCrossOrigin('anonymous').load(artProxyUrl(url), (t) => {
      if (this._coverPending !== token || !this._scene) { t.dispose(); return; }
      this._coverPending = null; if (this._cover) this._cover.dispose();
      photo(t); t.generateMipmaps = true; t.minFilter = THREE.LinearMipmapLinearFilter; this._cover = t;
      this._u.uCover.value = t; this._u.uHasCover.value = 1; this._u.uCoverAsp.value = (t.image.width || 1) / (t.image.height || 1); this._bus.uCover.value = t; this._bus.uHasCover.value = 1;
      // the mean colour, for the screens' light when they show the cover
      try { const cv = document.createElement('canvas'); cv.width = cv.height = 1; const g = cv.getContext('2d'); g.drawImage(t.image, 0, 0, 1, 1); const d = g.getImageData(0, 0, 1, 1).data; this._coverAvg.setRGB(d[0] / 255, d[1] / 255, d[2] / 255).convertSRGBToLinear(); } catch (e) { this._coverAvg.setRGB(0.3, 0.3, 0.3); }
    }, undefined, () => { if (this._coverPending === token) { this._coverPending = null; } });
  }

  // --- the shots -------------------------------------------------------------------------------
  _pickShot(name, bars) {
    if (this._forcedShot) name = this._forcedShot;
    if (!this._T.shots.includes(name)) name = 'wide';
    const S = SHOTS[name]; const z = Math.min(S.z, this._zoomCap);
    let c = S.c ? S.c.slice() : [this._dj ? this._dj.x : 0.5, this._dj ? this._dj.y - 0.02 : 0.5];
    // the view stays inside the image: half extents 0.5 / z (the output covers the source: same aspect assumed, else the larger)
    const hx = 0.5 / z * Math.max(1, this._aspect / (this._srcAspect || 16 / 9)), hy = 0.5 / z * Math.max(1, (this._srcAspect || 16 / 9) / this._aspect);
    c[0] = Math.min(Math.max(c[0], hx), 1 - hx); c[1] = Math.min(Math.max(c[1], hy), 1 - hy);
    this._shot = name; this._shotSince = this._time; this._shotBars = bars || 2;
    this._view.tc.set(c[0], c[1]); this._view.tz = z;
    this._view.c.copy(this._view.tc); this._view.z = z;      // a hard cut
    this._driftSeed = this._rng();
    this._say('plan ' + name + ' z' + z.toFixed(2));
  }

  // --- per frame -------------------------------------------------------------------------------
  update(deltaTime, profile) {
    const paused = profile.playbackState !== undefined && profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    this._clock.update(dt); this._adapt.update(dt, profile);
    if ((profile.artworkUrl || null) !== this._coverUrl) this._loadCover(profile.artworkUrl || null);
    const title = String(profile.title || '').trim(); if (title !== this._title) this._setTitle(title);
    const artist = String(profile.artist || '').trim(); if (artist !== this._artist) this._setArtist(artist);
    if (paused || !this._ready) {
      this._u.uFlash.value = 0;
      // never silently: assets that take long are said once (a black frame behind an admin lock reads as a refusal)
      if (!this._ready && !this._unavailable && !paused) { this._waitS = (this._waitS || 0) + dt; if (this._waitS > 4 && !this._waitSaid) { this._waitSaid = true; console.warn('[photostage] ' + this.cfg.id + ' : scène ' + this.cfg.scene + ' toujours en chargement après 4 s (textures/photostage.*)'); } }
      return;
    }
    this._time += dt;
    const a = this._adapt.get();
    const cap = this.cfg.intensityCap != null ? this.cfg.intensityCap : 1;
    const intensity = Math.min(a.intensity, cap);
    const calm = intensity < 0.5 ? 1 - intensity * 2 : 0;
    const director = profile.director || {};
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 126;
    this._beatPeriod = 60 / THREE.MathUtils.clamp(bpm, 60, 200);
    const bar = this._beatPeriod * 4;
    if (a.beat) { this._beatCount++; this._lastBeatAt = this._time; }
    if (a.bar) { this._barCount++; this._lastBarAt = this._time; }
    const bp = a.beatPhase != null ? a.beatPhase : 0;
    const kick = Math.pow(1 - bp, 3) * Math.max(0.35, a.pulse);
    // the build and the drop
    const ev = director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    let buildTarget = beatsToDrop != null && beatsToDrop < BUILD_BEATS * 2 ? clamp01((BUILD_BEATS * 2 - beatsToDrop) / (BUILD_BEATS * 2 - 4)) : (director.mode === 'build' ? 0.4 : 0);
    let drop = a.drop;
    if (this._dbgDrop) { const t = this._clock.t; buildTarget = Math.max(buildTarget, t < 3 ? t / 3 : 0); if (t >= 3 && this._dropAt < this._time - SCENE_S + 1) drop = true; }
    // the strong moment of every cycle (R20): a cycle that reaches 14 s without a drop builds and peaks on its own
    const t = this._clock.t;
    if (!this._dbgDrop && this._dropAt < this._cycleStart) { if (t > 10 && t < 14) buildTarget = Math.max(buildTarget, (t - 10) / 4); if (t >= 14 && !this._peakDone) { drop = true; this._peakDone = true; this._say('pic simulé'); } }
    this._build += (buildTarget - this._build) * Math.min(1, dt / 0.3);
    const since = this._time - this._dropAt, dropHot = since < bar * 2 ? 1 - since / (bar * 2) : 0;
    // the scene cycle: programme and palette, the cover on the centre at least once
    if (this._clock.wrapped) { this._cycleStart = this._time; this._palIndex++; this._palette = this._makePalette(this._palIndex); this._program = this.cfg.program.programs ? this.cfg.program.programs[this._palIndex % this.cfg.program.programs.length] : PROGRAMS[Math.floor(this._rng() * PROGRAMS.length)]; this._programAt = this._time; this._coverShown = false; this._peakDone = false; this._say('cycle : ' + this._program + ' / palette ' + this._palIndex); }
    if (a.phrase) { this._palIndex++; this._palette = this._makePalette(this._palIndex); this._say('phrase : palette ' + this._palIndex); }
    // the light programme changes every two bars: never still
    if (a.bar && this._time - this._programAt > bar * 1.9) { const list = this.cfg.program.programs || PROGRAMS; this._program = list[(list.indexOf(this._program) + 1 + Math.floor(this._rng() * (list.length - 1))) % list.length]; this._programAt = this._time; }
    // the VJ on the bar
    if (a.bar) {
      const shotAge = (this._time - this._shotSince) / bar;
      if (this._build > 0.6 && this._shot !== (this.cfg.shots.build || 'wide')) this._pickShot(this.cfg.shots.build || 'wide', 4);
      else if (shotAge >= this._shotBars - 0.01 && dropHot < 0.3) { const pool = calm > 0.5 ? (this.cfg.shots.calm || ['wide']) : (this.cfg.shots.prefer || ['wide']); this._pickShot(pool[Math.floor(this._rng() * pool.length)], calm > 0.5 ? 4 : 2 + Math.floor(this._rng() * 3)); }
      this._planScreens(calm, dropHot);
    }
    if (drop && since > bar) {
      this._dropAt = this._time; this._build = 0; this._jumpAt = this._time;
      const pick = this.cfg.shots.drop || ['dj', 'crowd']; this._pickShot(pick[Math.floor(this._rng() * pick.length)], 2);
      if (this.cfg.program.blinders) { this._blinder = 1; if (this._comfort.requestFlash(this._time)) this._flash = VDJ_DROP_FLASH * 0.6; }
      this._screenFlash = 1;
      if (this._pyro && this.cfg.program.flames && (this.cfg.program.flames !== 'rare' || this._rng() < 0.4)) this._fireFlames(1);
      if (this._confetti) this._fireConfetti();
      if (!this.cfg.program.blinders && !this.cfg.program.flames && this.cfg.program.co2) this._co2 = this._fx.map((p) => ({ x: p.x, y: p.y, at: this._time }));
      this._setContent('artist', true); this._layout = 0; this._lastLayout = 0; this._layoutAt = this._time; this._bg = 1; this._applyScreenModes();
      this._say('DROP');
    }
    if (this._build > 0.9 && buildTarget >= 0.99 && this.cfg.program.co2 && this._co2.every((c) => this._time - c.at > 3)) { this._co2 = this._fx.map((p) => ({ x: p.x, y: p.y, at: this._time })); this._say('CO2'); }
    // the flames on the strong beats of a hot drop
    if (this._pyro && this.cfg.program.flames === true && dropHot > 0.3 && a.beat && this._beatCount % 2 === 0) this._fireFlames(0.6);
    // the crowd: the rows bounce in blocks, the heads and hands on top, the phones in the calm
    const armsTarget = Math.max(0.3, this._build * 0.9, dropHot) * (this.cfg.program.arms != null ? this.cfg.program.arms : 1);
    this._arms += (armsTarget - this._arms) * Math.min(1, dt / 1.2);
    this._fieldU.uSway.value = (4 / 1080) * (0.5 + 0.5 * this._arms);
    const phonesTarget = calm * (this.cfg.program.phones != null ? this.cfg.program.phones : 0.6);
    this._phonesLvl += (phonesTarget - this._phonesLvl) * Math.min(1, dt / 2.0);
    this._updateCrowd(a, bp, intensity, calm, dt);
    // the lights
    this._updateLights(a, bp, intensity, calm, dropHot, dt);
    this._updateView(a, bp, dt);
    this._updateFg(a, bp, intensity, calm, dropHot, dt);
    // the screens: the countdown on the build's last 4 beats, the hit on every kick (+25 %), the white flash of the drop's first beat
    this._u.uCount.value = (beatsToDrop != null && beatsToDrop <= 4 && beatsToDrop > 0) ? Math.ceil(beatsToDrop) : 0;
    this._u.uScreenBright.value = (0.9 + 0.25 * kick + 0.2 * dropHot) * (1 - 0.15 * calm);
    this._screenFlash = Math.max(0, (this._screenFlash || 0) - dt / this._beatPeriod);
    this._u.uScreenFlash.value = Math.pow(this._screenFlash, 2);
    this._u.uKick.value = kick;
    this._u.uScroll.value = (this._time - this._layoutAt) * 0.12;
    this._u.uFillPhase.value = this._beatCount % 2;
    const eighths = Math.floor((this._time - this._layoutAt) / (this._beatPeriod * 0.5)) + 1;
    this._u.uRevealX.value = this._letterBounds.length ? (eighths >= this._letterBounds.length ? 1.0 : this._letterBounds[eighths - 1]) : 1.0;
    // uniforms
    const bands = a.bands || { low: 0.5, mid: 0.4, high: 0.4 };
    this._bus.uTime.value = this._time; this._bus.uBeats.value += dt / this._beatPeriod; this._bus.uBeatPhase.value = bp; this._bus.uBarPhase.value = (this._time - this._lastBarAt) / bar % 1; this._bus.uPulse.value = a.pulse * Math.pow(1 - bp, 2); this._bus.uBarPulse.value = Math.pow(1 - this._bus.uBarPhase.value, 3); this._bus.uEnergy.value = intensity; this._bus.uBarIndex.value = this._barCount; this._bus.uBands.value.set(bands.low, bands.mid, bands.high);
    this._bus.uCol0.value.copy(this._palette[0]); this._bus.uCol1.value.copy(this._palette[1 % this._palette.length]); this._bus.uCol2.value.copy(this._palette[2 % this._palette.length]);
    this._u.uTime.value = this._time; this._beamU.uTime.value = this._time; this._phonesU.uTime.value = this._time; this._fieldU.uTime.value = this._time;
    this._fieldU.uBeatPhase.value = bp; this._fieldU.uBeats.value = this._bus.uBeats.value; this._fieldU.uAmp.value = 0.35 * Math.max(0.3, a.pulse) * (0.5 + 0.5 * intensity) * (1 + 0.6 * this._build) * (this.cfg.program.bounce != null ? this.cfg.program.bounce : 1); this._fieldU.uJump.value = this._time - this._jumpAt < 1.5 ? 1 : 0; this._fieldU.uJumpT.value = this._time - this._jumpAt; this._fieldU.uCalm.value = calm;
    this._phonesU.uPhones.value = this._phonesLvl;
    this._viewU.uNod.value = 0.005 * Math.max(0.3, a.pulse) * Math.pow(1 - bp, 2);
    this._u.uFlash.value = this._flash; this._flash = 0;
    this._u.uFlameGlow.value = this._flameGlow;
    // comfort: the frame's estimated mean luminance from the layers' gains
    let est = 0.08 + 0.14 * intensity + 0.1 * dropHot + 0.3 * this._blinder + 0.15 * this._u.uScreenFlash.value;
    this._lumSince += dt; if (this._lumSince > 0.25) { this._u.uGain.value = this._comfort.measure(est, this._lumSince); this._lumSince = 0; }
    if (this._pyro) { this._pyro.update(dt, profile); }
    if (this._confetti) { this._confetti.update(dt, bands.high); if (this._camera) { this._confetti.setCamera(this._camera); const q = new THREE.Vector4(0, 0, -6.5, 1).applyMatrix4(this._camera.projectionMatrix); this._occU.uNdcZ.value = q.z / q.w; } if (this._confettiUntil > 0 && this._time > this._confettiUntil) { this._confettiVisible(false); this._confettiUntil = -1; } }
    this._post.update(vdjBeatChroma(bp, this._beatPeriod) * 0.4 + 0.6);
  }
  // the crowd's depth bands: 3 px at the back, 12 px at the front (1080p), each on its own phase, up from rest; x2.5 on the drop's two beats; a slow wide sway in the calm
  _updateCrowd(a, bp, intensity, calm, dt) {
    const px = 1 / 1080;
    const amps = [3, 5, 7, 9, 12];
    const gain = (0.6 + 0.4 * intensity) * (1 + 0.8 * this._build) * (this.cfg.program.bounce != null ? this.cfg.program.bounce : 1) * Math.max(0.35, a.pulse);
    const sinceJump = this._time - this._jumpAt;
    const jump = sinceJump >= 0 && sinceJump < this._beatPeriod * 2 ? 1 + 1.5 * Math.sin(Math.PI * sinceJump / (this._beatPeriod * 2)) : 1;
    for (let b = 0; b < BANDS; b++) {
      const ph = (bp + b * 0.12) % 1;
      const s = Math.sin(Math.PI * ph);
      const up = amps[b] * px * gain * s * s * jump;                       // positive: the source is sampled lower, the content rises
      const sway = calm * 4 * px * Math.sin(this._time * 0.8 + b * 1.1);  // the calm: a wide slow lateral sway instead
      this._viewU.uBand.value[b].set(sway, up * (1 - 0.8 * calm) + calm * 2 * px * (0.5 + 0.5 * Math.sin(this._time * 0.8 + b * 1.1)));
    }
  }
  // the screens' programme: the artist's name half of the time, the title a fifth, the cover a fifth, the generators the rest (more in the builds)
  _planScreens(calm, dropHot) {
    const t = this._clock.t;
    if (!this._coverShown && t > 2 && dropHot < 0.2) { this._setContent('cover', true); this._coverShown = true; this._say('écrans : pochette'); return; }
    const w = Object.assign({}, CONTENT_WEIGHTS);
    if (this._build > 0.5) { w.gen += 0.3; w.artist -= 0.2; w.cover -= 0.1; }
    if (!this._artist) { w.title += w.artist; w.artist = 0; }
    if (!this._title) { w.artist += w.title; w.title = 0; }
    if (!this._cover) { w.artist += w.cover; w.cover = 0; }
    const r = this._rng() * (w.artist + w.title + w.cover + w.gen);
    const pick = r < w.artist ? 'artist' : (r < w.artist + w.title ? 'title' : (r < w.artist + w.title + w.cover ? 'cover' : 'gen'));
    this._setContent(pick, false);
  }
  _setContent(kind, force) {
    this._content = kind;
    const mode = { gen: 0, cover: 1, title: 2, artist: 3 }[kind];
    this._screenModes = [mode, mode, mode, mode];
    if (kind === 'artist') {
      // a layout per bar, never twice in a row; STACKED needs vertical screens, PANORAMA three screens
      const ok = LAYOUTS.map((_, i) => i).filter((i) => i !== this._lastLayout && (i !== 5 || this._hasVertical) && (i !== 1 || this._u.uScreenN.value >= 3));
      this._layout = ok[Math.floor(this._rng() * ok.length)]; this._lastLayout = this._layout; this._layoutAt = this._time;
      this._bg = this._cover && this._rng() < 0.35 ? 2 : (this._rng() < 0.5 ? 1 : 0);
      const pal = this._palette; this._u.uTextCol.value.copy(this._rng() < 0.6 ? new THREE.Color(1, 1, 1) : pal[Math.floor(this._rng() * pal.length)]);
      this._say('écrans : ' + LAYOUTS[this._layout] + ' / fond ' + this._bg);
    } else if (kind === 'title') { this._bg = this._rng() < 0.5 ? 1 : 0; this._u.uTextCol.value.setRGB(1, 1, 1); }
    if (kind === 'gen') { this._generator = this._genNames[Math.floor(this._rng() * this._genNames.length)]; this._sideMap = this._sideMapFor(this._generator, Math.floor(this._rng() * 3)); }
    this._u.uLayout.value = this._layout; this._u.uBg.value = this._bg;
    this._applyScreenModes();
  }
  _sideMapFor(gen, want) { return (gen === 'tunnel' || gen === 'rays' || gen === 'shapes') && want === 0 ? 2 : want; }
  _applyScreenModes() {
    if (this._forcedScreens != null) this._screenModes = [this._forcedScreens, this._forcedScreens, this._forcedScreens, this._forcedScreens];
    const fl = urlParam('stagelayout'); if (fl && LAYOUTS.includes(fl)) { this._layout = LAYOUTS.indexOf(fl); this._u.uLayout.value = this._layout; }
    for (let k = 0; k < this._u.uScreenN.value; k++) { const inf = this._u.uScreenInfo.value[k]; inf.y = this._screenModes[inf.x | 0]; }
    this._u.uSideMap.value = this._sideMap;
  }
  _updateLights(a, bp, intensity, calm, dropHot, dt) {
    const P = this.cfg.program, pal = this._palette;
    const beat = Math.pow(1 - bp, 2) * Math.max(0.35, a.pulse);
    const t = this._time;
    // THE ZONES: saturated palette colours that change every beat (a chase), levels up to 2.5 at the peak; a sixteenth strobe at the end of a build; blinders white
    const lvl = this._u.uZoneLvl.value, zc = this._u.uZoneCol.value;
    const peak = Math.min(2.5, (0.5 + 1.0 * intensity) * (1 + 0.6 * this._build + 0.5 * dropHot));
    const strobeOn = P.strobe && this._build > 0.7;
    const sixteenth = Math.floor(t / (this._beatPeriod / 4));
    for (let z = 0; z < 6; z++) {
      const col = pal[(z + this._beatCount + Math.floor(z / 3)) % pal.length];
      const chase = ((z + this._beatCount) % 3 === 0) ? 1 : 0.45;
      let l = peak * (0.35 + 0.65 * chase * beat + 0.25 * (0.5 + 0.5 * Math.sin(t * 1.7 + z * 1.3))) * (1 - 0.6 * calm);
      if (strobeOn) l = (sixteenth + z) % 2 === 0 ? 2.2 : 0.05;
      if (this._blinder > 0 && z < 3) { zc[z].setRGB(1, 1, 1); l = this._blinder * 2.0; } else zc[z].copy(col);
      if (this._flameGlow > 0 && z >= 3) { zc[z].lerp(new THREE.Color(1, 0.45, 0.12), this._flameGlow * 0.8); l += this._flameGlow * 1.2; }
      lvl[z] = l;
    }
    this._blinder = Math.max(0, this._blinder - dt / 0.3);
    this._flameGlow = Math.max(0, this._flameGlow - dt / 1.4);
    // the screens' light: the colour of what they show, hit on the beat
    for (let g = 0; g < 4; g++) {
      const mode = this._screenModes[g];
      const c = this._u.uGroupCol.value[g];
      if (mode === 1 && this._cover) c.copy(this._coverAvg);
      else if (mode >= 2) { const bg = this._bg === 1 ? pal[(g + 1) % pal.length] : (this._bg === 2 && this._cover ? this._coverAvg : new THREE.Color(0.1, 0.1, 0.1)); c.copy(this._u.uTextCol.value).multiplyScalar(0.45).add(bg.clone().multiplyScalar(0.55)); }
      else c.copy(this._avgContent || pal[0]);
      const m = Math.max(c.r, c.g, c.b, 1e-3); c.multiplyScalar(1 / m);
      this._u.uGroupGain.value[g] = (1.2 + 0.7 * beat) * this._u.uScreenBright.value + 1.5 * this._u.uScreenFlash.value;
    }
    // THE BEAMS: every detected lens, in continuous motion (sweeps, fans opening and closing, crossings), never still
    const B = this._beamU; const n = Math.min(this._T.beams, this._lenses.length);
    const sel = this._lenses.slice(0, n);
    B.uN.value = n; B.uHaze.value = (P.haze != null ? P.haze : 0.5) * (0.6 + 0.5 * intensity);
    const spreadRange = P.beamSpread || [0.03, 0.1];
    const prog = this._program, pt = t - this._programAt;
    for (let i = 0; i < n; i++) {
      const L = sel[i]; const k = i / Math.max(1, n - 1) - 0.5;
      const down = Math.PI / 2;
      let ang;
      if (prog === 'fan') ang = down + k * 1.4 * Math.sin(t * 0.9);                                         // a fan opening and closing
      else if (prog === 'ballyhoo') ang = down + 0.9 * Math.sin(t * 2.2 + i * 0.7);
      else if (prog === 'cross') ang = down + (i % 2 ? 1 : -1) * (0.3 + 0.5 * (0.5 + 0.5 * Math.sin(t * 1.3)));
      else if (prog === 'converge') { const dx = (this._dj ? this._dj.x : 0.5) - L.x, dy = (this._dj ? this._dj.y : 0.55) - L.y; ang = Math.atan2(dy, dx * this._srcAspect) + 0.12 * Math.sin(t * 2.0 + i); }
      else if (prog === 'sweep') ang = down + 1.2 * Math.sin(t * 1.1 + k * 2.4);
      else ang = down + k * 0.9 + 0.35 * Math.sin(t * 0.8 + i * 0.3);
      if (this._build > 0.5) { const dx = (this._dj ? this._dj.x : 0.5) - L.x, dy = (this._dj ? this._dj.y : 0.55) - L.y; const target = Math.atan2(dy, dx * this._srcAspect); ang = ang + (target - ang) * smooth((this._build - 0.5) * 2); }
      if (L.level === 'floor') ang = -down + k * 0.7 + 0.3 * Math.sin(t * 1.4 + i);
      B.uO.value[i].set(L.x, L.y); B.uD.value[i].set(Math.cos(ang), Math.sin(ang));
      const col = pal[(i + this._beatCount) % pal.length];
      let level = (0.6 + 0.9 * intensity) * (0.6 + 0.4 * Math.sin(t * 1.9 + i * 0.9)) + beat * 0.9 + dropHot * 0.5;
      if (strobeOn) level = (sixteenth + i) % 2 === 0 ? 2.0 : 0.05;
      if (calm > 0) level *= 1 - 0.45 * calm;
      B.uC.value[i].set(col.r, col.g, col.b, level * (P.beamGain != null ? P.beamGain : 1));
      B.uSpread.value[i] = THREE.MathUtils.lerp(spreadRange[0], spreadRange[1], calm > 0 ? 1 : (0.5 + 0.5 * Math.sin(t * 0.5 + i * 0.4)));
    }
    // THE LASERS: fans from the second half of every cycle, the bar before the drop, and a TUNNEL from the DJ on the drop; sheets over the crowd
    const half = this._clock.t > SCENE_S * 0.5;
    const want = this._T.lasers > 0 && P.lasers ? (dropHot > 0 ? 1 : (this._build > 0.75 ? 1 : (half ? 0.7 : 0))) : 0;
    this._laserLevel += (want - this._laserLevel) * Math.min(1, dt / 0.25);
    B.uLaserN.value = this._laserLevel > 0.01 ? this._T.lasers : 0;
    B.uLaserCol.value.copy(pal[1 % pal.length]);
    const topL = this._lenses.filter((l) => l.level === 'top');
    const oy = topL.length ? Math.min(...topL.map((l) => l.y)) : 0.2;
    const tunnel = dropHot > 0.25 ? dropHot : 0;
    for (let i = 0; i < this._T.lasers; i++) {
      const k = i / Math.max(1, this._T.lasers - 1) - 0.5;
      if (tunnel > 0) {
        // the tunnel: rays from the DJ fanning out over the crowd and up into the truss
        const ang = Math.PI + (k + 0.5) * Math.PI + 0.15 * Math.sin(t * 3.0 + i);
        B.uLaser.value[i].set(this._dj ? this._dj.x : 0.5, this._dj ? this._dj.y : 0.55, ang, this._laserLevel * (0.6 + 0.4 * beat));
      } else {
        const ang = Math.PI / 2 + k * (1.7 + 0.5 * Math.sin(t * 0.9)) + 0.3 * Math.sin(t * 2.3 + i);
        B.uLaser.value[i].set(0.5 + k * 0.12, oy, ang, this._laserLevel * (0.5 + 0.5 * beat));
      }
    }
    const sheetWant = this._T.lasers > 0 && P.lasers ? Math.max(dropHot, half ? 0.35 : 0) : 0;
    this._sheet += (sheetWant - this._sheet) * Math.min(1, dt / 0.2);
    B.uSheet.value = this._sheet;
    // CO2
    B.uCo2N.value = this._co2.length;
    for (let i = 0; i < MAX_FX; i++) { const c = this._co2[i]; B.uCo2.value[i].set(c ? c.x : 0, c ? c.y : 0, c ? this._time - c.at : -1, c ? 1 : 0); }
  }
  _updateView(a, bp, dt) {
    const v = this._view; const t = this._time - this._shotSince;
    // slow move: a 2-4 % zoom over the shot, a pan, both within the cap; a 1 % kick on the beat
    const cap = this._zoomCap;
    const drift = Math.min(cap, v.tz * (1.0 + 0.025 * smooth(t / 8)));
    const kick = 1 + 0.01 * Math.pow(1 - bp, 3) * a.pulse;
    v.z = Math.min(cap, drift * kick);
    const hx = 0.5 / v.z * Math.max(1, this._aspect / (this._srcAspect || 16 / 9)), hy = 0.5 / v.z * Math.max(1, (this._srcAspect || 16 / 9) / this._aspect);
    const panX = (this._driftSeed - 0.5) * 0.04 * smooth(t / 6);
    v.c.set(Math.min(Math.max(v.tc.x + panX, hx), 1 - hx), Math.min(Math.max(v.tc.y, hy), 1 - hy));
    // parallax: the travelling's lateral offset, bounded 1 % (2 % doubled the heads), off on the DJ shot (the screen's depth is noisy there)
    const room = clamp01((v.z - 1) / 0.02);
    const par = this._T.parallax && this._shot !== 'dj' ? 0.01 * room : 0;
    this._viewU.uParallax.value.set(par * Math.sin(this._time * 0.25 + this._driftSeed * 6.28), par * 0.2 * Math.sin(this._time * 0.17));
  }
  _fireFlames(strength) {
    if (!this._pyro) return;
    const asp = this._aspect;
    for (const p of this._fx) {
      // image uv -> Pyro's clip space (x in [-asp, asp], y in [-1, 1]); the nozzle at the lip
      const o = this._imgToOut(p.x, p.y);
      const [sx, sy] = this._pyro.fromScreen((o.x * 2 - 1) * asp, 1 - o.y * 2);
      const n = { x: sx, y: sy, tilt: 0, dx: 0, dy: 1 };
      this._pyro.fireFlame(n);   // jets only: the gerb sparks read as frozen clusters at the lip
    }
    this._flameGlow = 1;
  }
  _fireConfetti() {
    if (!this._confetti || !this._camera) return;
    this._confettiVisible(true);
    this._confettiUntil = this._time + this._beatPeriod * 8 + 4;
    const cam = this._camera; let dist = 7;
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion), right = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion), up = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);
    const cannons = [];
    for (let k = 0; k < this._fx.length; k++) {
      const p = this._fx[k]; dist = 4.5 + 3.5 * (k / Math.max(1, this._fx.length - 1));
      const o = this._imgToOut(p.x, p.y);
      const h = Math.tan(THREE.MathUtils.degToRad(cam.fov || 50) / 2) * dist, w = h * this._aspect;
      const origin = cam.position.clone().add(fwd.clone().multiplyScalar(dist)).add(right.clone().multiplyScalar((o.x * 2 - 1) * w)).add(up.clone().multiplyScalar((1 - o.y * 2) * h));
      const dir = up.clone().multiplyScalar(1).add(right.clone().multiplyScalar((p.x - 0.5) * 1.4)).add(fwd.clone().multiplyScalar(0.6)).normalize();
      this._confetti.fire(origin, dir, 1.1, 1.0);
      cannons.push({ origin, direction: dir });
    }
    if (this.cfg.program.confetti === 'palette') this._confetti.setPalette(this._palette.map((c) => c.getHex()));
    this._confetti.fireRibbons(cannons);
    for (let i = 0; i < 3; i++) { const z = this._u.uZoneCol.value[i]; this._confetti.setLight(i, cam.position.clone().add(fwd.clone().multiplyScalar(2)).add(right.clone().multiplyScalar((i - 1) * 3)), fwd, z, 1.2 * this._u.uZoneLvl.value[i]); }
  }
  _confettiVisible(v) { const c = this._confetti; if (!c) return; if (c._pieces) c._pieces.visible = v; if (c._ribbons) c._ribbons.visible = v; if (this._occ) this._occ.visible = v; }
  _imgToOut(x, y) { const v = this._view; const s = this._viewU.uScale.value; return { x: (x - v.c.x) / s.x + 0.5, y: (y - v.c.y) / s.y + 0.5 }; }

  // --- render ----------------------------------------------------------------------------------
  _render(renderer) {
    if (!this._ready) return;
    renderer.getDrawingBufferSize(this._size);
    const prev = renderer.getRenderTarget();
    let W = this._size.x, H = this._size.y; if (prev) { W = prev.width; H = prev.height; }
    const aspect = W / Math.max(1, H);
    if (Math.abs(aspect - this._aspect) > 1e-3 || this._lastW !== W) {
      this._aspect = aspect; this._lastW = W;
      this._u.uAspect.value = aspect; this._beamU.uAspect.value = aspect; this._fieldU.uAspect.value = this._srcAspect || aspect;
      this._beamU.uOutPx.value.set(W, H); this._u.uOutPx.value.set(W, H);
      // R1: the zoom never exceeds source width / output width; a source narrower than the output caps the tight shots at ZOOM_SMALL_SOURCE (the brief's rule)
      const info = this._assets.info;
      const ratio = info.size[0] / W;
      this._zoomCap = ratio >= ZOOM_SMALL_SOURCE ? ratio : ZOOM_SMALL_SOURCE;
      // a magnified photograph gets a light unsharp mask, proportional to the magnification
      this._u.uSharp.value = ratio < 1 ? Math.min(0.22, 0.3 * (1 - ratio)) : 0;
      if (ratio < 1 && !this._capSaid) { this._capSaid = true; console.info('[photostage] ' + this.cfg.id + ' : source ' + info.size[0] + ' px pour ' + W + ' px de sortie (x' + (1 / ratio).toFixed(2) + ') : zoom des plans serrés limité à ' + ZOOM_SMALL_SOURCE + ', netteté par masque flou ' + this._u.uSharp.value.toFixed(2)); }
      if (this._rtBeams.width !== (W >> 1)) this._rtBeams.setSize(Math.max(2, W >> 1), Math.max(2, H >> 1));
      this._pickShot(this._shot, this._shotBars);
    }
    // the view's scale: the source covers the output (R18), zoom z
    const v = this._view; const sa = this._srcAspect || 16 / 9;
    const sx = (1 / v.z) * Math.min(1, aspect / sa), sy = (1 / v.z) * Math.min(1, sa / aspect);
    this._viewU.uScale.value.set(sx, sy);
    if (this._titleDirty && this._font) { this._titleDirty = false; const sp = [0.03, 0.97]; this._drawText(renderer, this._rtTitle, this._title, this._font, sp); this._u.uTitleSpan.value.set(sp[0], sp[1]); }
    if (this._artistDirty && (this._fontBold || this._font)) { this._artistDirty = false; const f = this._fontBold || this._font; this._drawText(renderer, this._rtArtist, this._artist, f, this._artistSpan); this._u.uArtistSpan.value.set(this._artistSpan[0], this._artistSpan[1]); this._computeLetterBounds(this._artist, f); this._drawStacked(renderer, this._artist || this._title, f); }
    if (this._digitsDirty && this._font) { this._digitsDirty = false; this._drawDigits(renderer); }
    // 1. the content targets (A: the current generator; B: another, for 'different' side screens)
    const gens = this._genNames;
    this._passQuad.material = this._gens[this._generator] || this._gens.tunnel; renderer.setRenderTarget(this._rtA); renderer.render(this._passScene, this._ortho);
    this._passQuad.material = this._gens[gens[(gens.indexOf(this._generator) + 1) % gens.length]] || this._gens.plasma; renderer.setRenderTarget(this._rtB); renderer.render(this._passScene, this._ortho);
    // the mean colour of the content: the smallest mip, read back every 10 frames (2 x 1 px)
    if (!this._avgContent) this._avgContent = new THREE.Color(0.3, 0.3, 0.3);
    this._avgN = (this._avgN || 0) + 1;
    if (this._avgN % 10 === 0) {
      if (!this._avgMat) { this._avgMat = new THREE.ShaderMaterial({ uniforms: { uTex: { value: this._rtA.texture } }, vertexShader: quadVertex, fragmentShader: 'precision highp float; uniform sampler2D uTex; varying vec2 vUv; void main() { gl_FragColor = vec4(texture2D(uTex, vUv, 9.0).rgb, 1.0); }', depthTest: false, depthWrite: false }); this._avgBuf = new Uint8Array(8); }
      this._passQuad.material = this._avgMat; renderer.setRenderTarget(this._rtAvg); renderer.render(this._passScene, this._ortho);
      try { renderer.readRenderTargetPixels(this._rtAvg, 0, 0, 2, 1, this._avgBuf); this._avgContent.setRGB((this._avgBuf[0] + this._avgBuf[4]) / 510, (this._avgBuf[1] + this._avgBuf[5]) / 510, (this._avgBuf[2] + this._avgBuf[6]) / 510); } catch (e) { /* keep the last */ }
    }
    // 2. the bounce field (source space)
    renderer.setRenderTarget(this._rtField); renderer.setClearColor(0x000000, 1); renderer.clear(true, false, false);
    this._fieldU.uFieldH.value = this._rtField.height;
    renderer.render(this._fieldScene, this._ortho);
    // 3. the beams (half res, output space)
    this._passQuad.material = this._beamMat; renderer.setRenderTarget(this._rtBeams); renderer.render(this._passScene, this._ortho);
    renderer.setRenderTarget(prev);
    // the pyro layer follows the view
    if (this._pyro) { this._pyro.setResolution(W, H); }
  }

  get probe() {
    return {
      id: this.cfg.id, tier: this._tier, ready: this._ready, unavailable: this._unavailable, shot: this._shot, zoom: +this._view.z.toFixed(3), zoomCap: +this._zoomCap.toFixed(3), program: this._program, palette: this._palIndex,
      build: +this._build.toFixed(2), content: this._content, layout: LAYOUTS[this._layout], bg: this._bg, arms: +this._arms.toFixed(2), phones: +this._phonesLvl.toFixed(2), hands: this._handCount || 0, sharp: +this._u.uSharp.value.toFixed(2), screens: this._screenModes.slice(), generator: this._generator, sideMap: this._sideMap, lasers: +this._laserLevel.toFixed(2), co2: this._co2.length,
      beams: this._beamU.uN.value, fg: this._fg ? { people: this._fg.slots.length, dupes: this._fg.dupes, armsUp: +(this._fg.slots.reduce((q, s) => q + s.arm, 0) / Math.max(1, this._fg.slots.length)).toFixed(2) } : null, scene: +this._clock.t.toFixed(2), gain: +this._u.uGain.value.toFixed(2), decisions: this._decisions.slice(-8), intensity: this._adapt.get().intensity
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); }
    if (this._phones && this._scene) { this._scene.remove(this._phones); this._phones.geometry.dispose(); }
    if (this._occ && this._scene) { this._scene.remove(this._occ); this._occ.geometry.dispose(); }
    if (this._fieldPts) this._fieldPts.geometry.dispose();
    if (this._fg) { if (this._scene) { this._scene.remove(this._fg.mesh); if (this._fg.atlasMesh) this._scene.remove(this._fg.atlasMesh); } this._fg.geo.dispose(); this._fg.mat.dispose(); this._fg.stateTex.dispose(); if (this._fg.atlasMesh) { this._fg.atlasMesh.geometry.dispose(); this._fg.atlasMesh.material.dispose(); } this._fg = null; }
    if (this._passQuad) this._passQuad.geometry.dispose();
    for (const m of [this._mat, this._beamMat, this._fieldMat, this._phonesMat, this._occMat, this._avgMat, this._glyphMat]) if (m) m.dispose();
    for (const k in this._gens || {}) this._gens[k].dispose();
    for (const t of [this._rtA, this._rtB, this._rtAvg, this._rtBeams, this._rtField, this._rtTitle, this._rtDigits, this._rtArtist, this._rtStack]) if (t) t.dispose();
    if (this._noise) this._noise.dispose();
    if (this._cover) this._cover.dispose(); this._cover = null;
    if (this._pyro) this._pyro.dispose(); if (this._confetti) this._confetti.dispose();
    if (this._assets) this._disposeAssets(this._assets);
    this._assets = null; this._ready = false; this._scene = null;
  }
}
